(function () {
  'use strict';

  const GROUP_DEFINITIONS = Object.freeze([
    Object.freeze({ id: 'design', label: 'DESIGN', fields: ['style', 'materialCategory', 'material', 'materialColour', 'keeperColourMode', 'keeper1Colour', 'keeper2Colour', 'stitchingStyle', 'threadColour', 'tipShape', 'liningMaterial', 'liningColour'] }),
    Object.freeze({ id: 'fit', label: 'FIT', fields: ['curvedEnd', 'lugWidth', 'customLugWidthMm', 'buckleWidth', 'customBuckleWidthMm', 'lengthPreset', 'customLengthLongEnd', 'customLengthShortEnd', 'wristLengthMm', 'watchLugToLugMm', 'wingsCutDimensionsCenterWidth', 'wingsCutDimensionsCutDepth', 'middlesCutDimensionsCutWidth', 'middlesCutDimensionsCutDepth', 'buckle', 'buckleUpload', 'springBars'] }),
    Object.freeze({ id: 'construction', label: 'CONSTRUCTION', fields: ['padding', 'thickness', 'foldingThickness', 'customThicknessLug', 'customThicknessTip', 'edgeFinishing', 'edgeColour'] }),
    Object.freeze({ id: 'finish', label: 'FINISH', fields: ['personalisation', 'noCernirLogo', 'relatedImages', 'notes'] })
  ]);
  const GROUP_BY_FIELD = new Map(GROUP_DEFINITIONS.flatMap((group) => group.fields.map((field) => [field, group.id])));
  const MATERIAL_SWATCH_ALIASES = Object.freeze({
    'calfskin-epsom': Object.freeze(['epsom', 'epsom-calf']),
    'goatskin-alran-chevre': Object.freeze(['alran-chevre', 'alran-chevre-sully', 'alran-chevre-fat-nat'])
  });

  function inputValue(input) {
    if (input.type === 'checkbox') return input.checked;
    if (input.type === 'file') return Array.from(input.files || []);
    return input.value || null;
  }

  const moneyFormats = new Map();
  function formatMoney(cents, currencyCode) {
    const amount = Number(cents || 0) / 100;
    const hasMinorUnits = Number(cents || 0) % 100 !== 0;
    const key = `${currencyCode || 'USD'}:${hasMinorUnits}`;
    try {
      if (!moneyFormats.has(key)) {
        moneyFormats.set(key, new Intl.NumberFormat(document.documentElement?.lang || 'en', {
          style: 'currency',
          currency: currencyCode || 'USD',
          minimumFractionDigits: hasMinorUnits ? 2 : 0,
          maximumFractionDigits: 2
        }));
      }
      return moneyFormats.get(key).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(hasMinorUnits ? 2 : 0)}`;
    }
  }

  // The design in progress, kept for this browser tab only (sessionStorage) and only for the same page address,
  // so a link with ?leather=… still opens that leather. Files are left out: a browser can't store them.
  const DESIGN_KEY = 'cernir-custom-strap-design';
  function readSavedDesign() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(DESIGN_KEY) || 'null');
      return saved?.search === window.location.search && saved.rawState && typeof saved.rawState === 'object' ? saved.rawState : null;
    } catch (_error) {
      return null;
    }
  }
  function saveDesign(rawState) {
    try {
      const { buckleUpload: _buckleUpload, relatedImages: _relatedImages, ...design } = rawState;
      window.sessionStorage.setItem(DESIGN_KEY, JSON.stringify({ search: window.location.search, rawState: design }));
    } catch (_error) {}
  }

  // Every option change re-renders the whole form; writing only what changed keeps that to a handful of DOM
  // mutations instead of several hundred (rewriting an identical value still counts as a mutation).
  function setData(element, key, value) {
    if (element && element.dataset[key] !== value) element.dataset[key] = value;
  }
  function setFlag(element, property, value) {
    if (element && element[property] !== value) element[property] = value;
  }
  function setText(element, value) {
    if (element && element.textContent !== String(value ?? '')) element.textContent = value;
  }
  function setAttr(element, name, value) {
    if (element && element.getAttribute(name) !== String(value)) element.setAttribute(name, value);
  }

  function normalizeCatalogKey(value) {
    return String(value || '').trim().toLowerCase().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function leatherDisplayLabel(value, category) {
    const label = String(value || '').trim();
    if (normalizeCatalogKey(label) === 'alligator-round-scales') return label;
    if (normalizeCatalogKey(category) === 'non-leather') return label;
    return /\bleather\b/i.test(label) ? label : `${label} Leather`;
  }

  function createShopifyMaterialCatalog(root, fallbackApi, stateApi) {
    const entries = root.querySelector('[data-cs-cd-shopify-material-swatches]')?.content?.querySelectorAll('[data-shopify-material-entry]') || [];
    const materialRecords = new Map();

    Array.from(entries).forEach((entry) => {
      const materialId = normalizeCatalogKey(entry.dataset.materialKey);
      const colourId = normalizeCatalogKey(entry.dataset.colourKey || entry.dataset.colourLabel);
      if (!materialId || !colourId) return;

      if (!materialRecords.has(materialId)) {
        materialRecords.set(materialId, {
          id: materialId,
          label: leatherDisplayLabel(entry.dataset.materialLabel || entry.dataset.materialName || materialId, entry.dataset.materialCategory),
          category: normalizeCatalogKey(entry.dataset.materialCategory || 'Regular Leather'),
          basePriceCents: Number(entry.dataset.materialBasePriceCents),
          colourInventory: [],
          colourIds: new Set()
        });
      }

      const material = materialRecords.get(materialId);
      if (!material.colourIds.has(colourId)) {
        material.colourIds.add(colourId);
        material.colourInventory.push({ id: colourId, label: entry.dataset.colourLabel || entry.dataset.colourKey });
      }
    });

    if (!materialRecords.size) return fallbackApi;

    const activeCategoryIds = new Set(Array.from(materialRecords.values(), (material) => material.category));
    const categories = fallbackApi.DATA.categories.filter((category) => activeCategoryIds.has(category.id));
    const categoryOrder = new Map(categories.map((category, index) => [category.id, index]));
    const materials = Array.from(materialRecords.values(), ({ colourIds: _colourIds, ...material }) => material)
      .sort((left, right) => (categoryOrder.get(left.category) ?? 999) - (categoryOrder.get(right.category) ?? 999)
        || left.label.localeCompare(right.label));
    const materialsById = new Map(materials.map((material) => [material.id, material]));
    const getMaterials = (categoryId) => materials.filter((material) => material.category === categoryId);
    const getMaterial = (materialId) => materialsById.get(materialId) || null;
    const getColours = (materialId) => getMaterial(materialId)?.colourInventory || [];
    const isMaterialInCategory = (materialId, categoryId) => getMaterial(materialId)?.category === categoryId;
    const isColourForMaterial = (colourId, materialId) => getColours(materialId).some((colour) => colour.id === colourId);

    return Object.freeze({
      DATA: Object.freeze({ ...fallbackApi.DATA, categories, materials }),
      getMaterials,
      getMaterial,
      getColours,
      isMaterialInCategory,
      isColourForMaterial,
      transition(rawState, _stateApi, field, value) {
        let next = stateApi.updateRawState(rawState, field, value);
        // A new leather resets the edge paint to tone-on-tone; a new colour of the same leather keeps the chosen paint.
        if (['materialCategory', 'material'].includes(field) && value !== rawState[field]) {
          next = stateApi.updateRawState(next, 'edgeColour', 'on-tone');
        }
        if (field === 'materialCategory' && !isMaterialInCategory(next.material, value)) {
          next = stateApi.updateRawState(next, 'material', null);
          next = stateApi.updateRawState(next, 'materialColour', null);
        }
        if (field === 'material') {
          const firstColour = getColours(value)[0]?.id || null;
          next = stateApi.updateRawState(next, 'materialColour', firstColour);
        }
        if (field === 'materialColour' && !isColourForMaterial(value, next.material)) {
          next = stateApi.updateRawState(next, 'materialColour', null);
        }
        return next;
      }
    });
  }

  // Builder funnel tracking. The Meta pixel (fbq) and GA4 (gtag) are already loaded on the page, so events
  // go to them directly; Shopify.analytics.publish also exposes them to Customer events pixels.
  // Each event fires at most once per page view. Skipped when the visitor has declined analytics/marketing.
  const trackedBuilderEvents = new Set();
  const BUILDER_EVENTS = Object.freeze({
    customize_start: { meta: ['track', 'CustomizeProduct'], ga4: 'custom_strap_start' },
    step_fit: { ga4: 'custom_strap_step', params: { step: 2, step_name: 'fit' } },
    step_construction: { ga4: 'custom_strap_step', params: { step: 3, step_name: 'construction' } },
    step_finish: { meta: ['trackCustom', 'CustomStrapFinishStep'], ga4: 'custom_strap_step', params: { step: 4, step_name: 'finish' } },
    preview_enlarge: { meta: ['trackCustom', 'CustomStrapPreviewEnlarge'], ga4: 'custom_strap_preview_enlarge' }
  });

  function trackingAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    try {
      if (typeof privacy?.analyticsProcessingAllowed === 'function' && !privacy.analyticsProcessingAllowed()) return false;
    } catch (_error) {}
    return true;
  }

  function marketingAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    try {
      if (typeof privacy?.marketingAllowed === 'function' && !privacy.marketingAllowed()) return false;
    } catch (_error) {}
    return true;
  }

  function trackBuilderEvent(key, extra) {
    const definition = BUILDER_EVENTS[key];
    if (!definition || trackedBuilderEvents.has(key) || !trackingAllowed()) return;
    trackedBuilderEvents.add(key);
    const params = { content_name: 'Custom Leather Watch Strap', content_type: 'product', ...(definition.params || {}), ...(extra || {}) };
    try {
      if (definition.meta && marketingAllowed() && typeof window.fbq === 'function') window.fbq(definition.meta[0], definition.meta[1], params);
    } catch (_error) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', definition.ga4, params);
    } catch (_error) {}
    try {
      window.Shopify?.analytics?.publish?.('cernir_custom_strap', { action: key, ...params });
    } catch (_error) {}
  }

  window.CernirCustomStrapTrack = trackBuilderEvent;

  class CustomStrapCd {
    constructor(root, stateApi, preconfigurationApi, styleDataApi, liningDataApi, dimensionsDataApi, paddingThicknessDataApi, stitchingDataApi, finishingHardwareDataApi, buckleDataApi, additionalCustomisationDataApi, commerceApi) {
      this.root = root;
      this.stateApi = stateApi;
      this.preconfigurationApi = createShopifyMaterialCatalog(root, preconfigurationApi, stateApi);
      this.styleDataApi = styleDataApi;
      this.liningDataApi = liningDataApi;
      this.dimensionsDataApi = dimensionsDataApi;
      this.paddingThicknessDataApi = paddingThicknessDataApi;
      this.stitchingDataApi = stitchingDataApi;
      this.finishingHardwareDataApi = finishingHardwareDataApi;
      this.buckleDataApi = buckleDataApi;
      this.additionalCustomisationDataApi = additionalCustomisationDataApi;
      this.commerceApi = commerceApi;
      this.optionImageAssets = new Map(Array.from(root.querySelectorAll('[data-cs-cd-option-image-asset]')).map((item) => [
        `${item.dataset.optionGroup}:${item.dataset.optionId}`,
        item.dataset.imageUrl || ''
      ]));
      this.optionFullImageAssets = new Map(Array.from(root.querySelectorAll('[data-cs-cd-option-image-asset]')).map((item) => [
        `${item.dataset.optionGroup}:${item.dataset.optionId}`,
        item.dataset.fullImageUrl || item.dataset.imageUrl || ''
      ]));
      this.rawState = stateApi.createRawState();
      const incoming = new URLSearchParams(window.location.search);
      const requestedCategory = incoming.get('material_category');
      const requestedMaterial = incoming.get('leather');
      const requestedColour = incoming.get('colour');
      const requestedThread = incoming.get('stitch');
      if (preconfigurationApi.DATA.categories.some((item) => item.id === requestedCategory)) {
        this.rawState = this.preconfigurationApi.transition(this.rawState, stateApi, 'materialCategory', requestedCategory);
      }
      if (requestedMaterial && this.preconfigurationApi.isMaterialInCategory(requestedMaterial, this.rawState.materialCategory)) {
        this.rawState = this.preconfigurationApi.transition(this.rawState, stateApi, 'material', requestedMaterial);
      }
      if (requestedColour && this.preconfigurationApi.isColourForMaterial(requestedColour, this.rawState.material)) {
        this.rawState = this.preconfigurationApi.transition(this.rawState, stateApi, 'materialColour', requestedColour);
      }
      const requestedThreadOption = requestedThread ? stitchingDataApi?.getOption('threadColour', requestedThread) : null;
      if (requestedThreadOption && requestedThreadOption.disabled !== true) {
        this.rawState = stateApi.updateRawState(this.rawState, 'threadColour', requestedThread);
      }
      // A reload (or reopening the page in the same tab) keeps the design being built; uploaded files can't be kept.
      const savedDesign = readSavedDesign();
      if (savedDesign) this.rawState = stateApi.createRawState({ ...savedDesign, buckleUpload: [], relatedImages: [] });
      if (paddingThicknessDataApi?.normalizeState) this.rawState = paddingThicknessDataApi.normalizeState(this.rawState);
      this.effectiveState = stateApi.toEffectiveState(this.rawState, this.preconfigurationApi, liningDataApi, dimensionsDataApi, paddingThicknessDataApi, stitchingDataApi, finishingHardwareDataApi, buckleDataApi, additionalCustomisationDataApi, styleDataApi);
      this.derivedState = stateApi.toDerivedState(this.rawState, this.effectiveState, this.preconfigurationApi, liningDataApi, dimensionsDataApi, paddingThicknessDataApi, stitchingDataApi, finishingHardwareDataApi, buckleDataApi, additionalCustomisationDataApi, styleDataApi);
      this.materialCategorySelect = root.querySelector('[data-cs-cd-material-categories]');
      this.materialSelectors = root.querySelector('[data-cs-cd-material-selectors]');
      this.materialCategoryCurrent = root.querySelector('[data-cs-cd-material-category-current]');
      this.materialField = root.querySelector('[data-cs-cd-material-field]');
      this.materialLabel = root.querySelector('[data-cs-cd-material-label]');
      this.materialSelect = root.querySelector('[data-cs-cd-material]');
      this.materialColourField = root.querySelector('[data-cs-cd-material-colour-field]');
      this.materialColourLabel = root.querySelector('[data-cs-cd-material-colour-label]');
      this.materialColourGrid = root.querySelector('[data-cs-cd-material-colours]');
      this.materialHelper = root.querySelector('[data-cs-cd-material-helper]');
      this.colourHelper = root.querySelector('[data-cs-cd-colour-helper]');
      this.liningMaterialSelect = root.querySelector('[data-cs-cd-lining-materials]');
      this.liningColourGrid = root.querySelector('[data-cs-cd-lining-colours]');
      this.liningColourLabel = root.querySelector('[data-cs-cd-lining-colour-label]');
      this.liningColourHelper = root.querySelector('[data-cs-cd-lining-colour-helper]');
      this.outerMaterialErrors = Object.fromEntries(['materialCategory', 'material', 'materialColour'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.outerMaterialValidationAttempted = false;
      this.liningValidationAttempted = false;
      this.liningErrors = Object.fromEntries(['liningMaterial', 'liningColour'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.keeperModeSelect = root.querySelector('[data-cs-cd-keeper-colour-mode]');
      this.keeperColours = root.querySelector('[data-cs-cd-keeper-colours]');
      this.keeperHelper = root.querySelector('[data-cs-cd-keeper-helper]');
      this.keeperColourGrids = Object.fromEntries(['keeper1Colour', 'keeper2Colour'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-keeper-colour-grid="${field}"]`)
      ]));
      this.keeperValidationAttempted = false;
      this.keeperErrors = Object.fromEntries(['keeperColourMode', 'keeper1Colour', 'keeper2Colour'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.shopifySwatches = this.readShopifySwatches();
      this.dimensionValidationAttempted = false;
      this.dimensionErrorOrder = Object.freeze([
        'lugWidth', 'customLugWidthMm', 'buckleWidth', 'customBuckleWidthMm',
        'lengthPreset', 'customLengthLongEnd', 'customLengthShortEnd',
        'wristLengthMm', 'watchLugToLugMm',
        'wingsCutDimensionsCenterWidth', 'wingsCutDimensionsCutDepth',
        'middlesCutDimensionsCutWidth', 'middlesCutDimensionsCutDepth'
      ]);
      this.dimensionErrors = Object.fromEntries(this.dimensionErrorOrder.map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.paddingThicknessValidationAttempted = false;
      this.paddingThicknessErrorOrder = Object.freeze(['padding', 'thickness', 'customThicknessLug', 'customThicknessTip', 'foldingThickness']);
      this.paddingThicknessErrors = Object.fromEntries(this.paddingThicknessErrorOrder.map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.customLengthInputs = Object.fromEntries(['longEndMm', 'shortEndMm'].map((component) => [
        component,
        root.querySelector(`[data-cs-cd-custom-length-component="${component}"]`)
      ]));
      this.lengthGuide = root.querySelector('[data-cs-cd-length-guide]');
      this.lengthGuideOpenButton = root.querySelector('[data-cs-cd-length-guide-open]');
      this.lengthGuideTrigger = null;
      this.fieldGuide = root.querySelector('[data-cs-cd-field-guide]');
      this.fieldGuideTitle = root.querySelector('[data-cs-cd-field-guide-title]');
      this.fieldGuideBody = root.querySelector('[data-cs-cd-field-guide-body]');
      this.fieldGuideOpenButtons = Array.from(root.querySelectorAll('[data-cs-cd-field-guide-open]'));
      this.fieldGuideTrigger = null;
      this.namedMeasurementInputs = Array.from(root.querySelectorAll('[data-cs-cd-named-measurement-field]'));
      this.customThicknessInputs = Object.fromEntries(['lugMm', 'tipMm'].map((component) => [
        component,
        root.querySelector(`[data-cs-cd-custom-thickness-component="${component}"]`)
      ]));
      this.stitchingValidationAttempted = false;
      this.stitchingErrors = Object.fromEntries(['stitchingStyle', 'threadColour'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.threadColourModeSelect = root.querySelector('[data-cs-cd-thread-colour-mode]');
      this.threadColourPicker = root.querySelector('[data-cs-cd-thread-colour-picker]');
      this.threadColourMode = ['tone-on-tone', 'cream'].includes(this.rawState.threadColour)
        ? this.rawState.threadColour
        : (this.rawState.threadColour ? 'choose-another-colour' : '');
      this.threadColourAssets = new Map(Array.from(root.querySelectorAll('[data-cs-cd-thread-colour-asset]')).map((item) => [
        item.dataset.csCdThreadColourAsset,
        { imageUrl: item.dataset.imageUrl || '', fullImageUrl: item.dataset.fullImageUrl || item.dataset.imageUrl || '' }
      ]));
      this.finishingHardwareValidationAttempted = false;
      this.finishingHardwareErrorOrder = Object.freeze(['tipShape', 'edgeFinishing', 'edgeColour', 'springBars']);
      this.finishingHardwareErrors = Object.fromEntries(this.finishingHardwareErrorOrder.map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.edgeColourModeSelect = root.querySelector('[data-cs-cd-edge-colour-mode]');
      this.edgeColourPicker = root.querySelector('[data-cs-cd-edge-colour-picker]');
      this.edgeColourAssets = new Map(Array.from(root.querySelectorAll('[data-cs-cd-edge-colour-asset]')).map((item) => [
        item.dataset.csCdEdgeColourAsset,
        item.dataset.imageUrl
      ]));
      this.edgeColourMode = this.rawState.edgeColour === 'on-tone' ? 'on-tone' : 'choose-another-colour';
      this.customEdgeColourDraft = this.rawState.edgeColour && this.rawState.edgeColour !== 'on-tone' ? this.rawState.edgeColour : null;
      this.buckleValidationAttempted = false;
      this.buckleErrorOrder = Object.freeze(['buckle', 'buckleUpload']);
      this.buckleErrors = Object.fromEntries(this.buckleErrorOrder.map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.buckleAssets = new Map(Array.from(root.querySelectorAll('[data-cs-cd-buckle-asset]')).map((item) => [
        item.dataset.csCdBuckleAsset,
        item.dataset.imageUrl || ''
      ]));
      this.activeBuckleType = 'classic';
      this.additionalCustomisationValidationAttempted = false;
      this.coreValidationAttempted = false;
      this.additionalCustomisationErrorOrder = Object.freeze(['relatedImages', 'personalisation']);
      this.additionalCustomisationErrors = Object.fromEntries(this.additionalCustomisationErrorOrder.map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.relatedImagesInput = root.querySelector('[data-cs-cd-state-field="relatedImages"]');
      this.relatedImagesSelection = root.querySelector('[data-cs-cd-related-images-selection]');
      this.relatedImagesList = root.querySelector('[data-cs-cd-related-images-list]');
      this.personalisationInput = root.querySelector('[data-cs-cd-state-field="personalisation"]');
      this.orderForm = root.querySelector('[data-cs-cd-order-form]');
      this.orderButton = root.querySelector('[data-cs-cd-order-submit]');
      this.orderButtonLabel = this.orderButton?.dataset.submitLabel || 'Add to cart';
      this.orderStatus = root.querySelector('[data-cs-cd-order-status]');
      this.priceContainer = root.querySelector('[data-cs-cd-price]');
      this.priceValue = root.querySelector('[data-cs-cd-price-value]');
      this.summaryToggle = root.querySelector('[data-cs-cd-summary-toggle]');
      this.summaryToggleLabel = root.querySelector('[data-cs-cd-summary-toggle-label]');
      this.summaryBody = root.querySelector('[data-cs-cd-summary-body]');
      this.summaryTotals = Array.from(root.querySelectorAll('[data-cs-cd-summary-total]'));
      this.minimumPriceCents = Number(root.dataset.minimumPriceCents || 0);
      this.currencyCode = root.dataset.currencyCode || commerceApi.DATA?.currencyCode || 'USD';
      this.priceBreakdown = null;
      this.variants = commerceApi.parseJsonScript(root.querySelector('[data-cs-cd-variants]'), []);
      this.commerceContract = commerceApi.parseJsonScript(root.querySelector('[data-cs-cd-commerce-contract]'), { materials: [] });
      this.materialCatalogAvailable = Boolean(root.querySelector('[data-cs-cd-shopify-material-swatches]')?.content?.querySelector('[data-shopify-material-entry]'));
      this.isSubmitting = false;
      this.coreErrors = Object.fromEntries(['style', 'curvedEnd'].map((field) => [
        field,
        root.querySelector(`[data-cs-cd-field-error="${field}"]`)
      ]));
      this.styleZoom = root.querySelector('[data-cs-cd-style-zoom]');
      this.styleZoomPreview = root.querySelector('[data-cs-cd-style-zoom-preview]');
      this.styleZoomCaption = root.querySelector('[data-cs-cd-style-zoom-caption]');
      this.styleZoomClose = root.querySelector('[data-cs-cd-style-zoom-close]');
      this.zoomPortals = new Map();
      this.styleZoomTrigger = null;
      this.styleCurrent = root.querySelector('[data-cs-cd-style-current]');
      this.curvedEndCurrent = root.querySelector('[data-cs-cd-curved-end-current]');
      this.paddingCurrent = root.querySelector('[data-cs-cd-padding-current]');
      this.styleCarousel = root.querySelector('[data-cs-cd-option-grid="style"]');
      this.styleCarouselPrevious = root.querySelector('[data-cs-cd-style-carousel-previous]');
      this.styleCarouselNext = root.querySelector('[data-cs-cd-style-carousel-next]');
      this.stitchingStyleCurrent = root.querySelector('[data-cs-cd-stitching-style-current]');
      this.stitchingStyleCarousel = root.querySelector('[data-cs-cd-option-grid="stitchingStyle"]');
      this.stitchingStyleCarouselPrevious = root.querySelector('[data-cs-cd-stitching-style-carousel-previous]');
      this.stitchingStyleCarouselNext = root.querySelector('[data-cs-cd-stitching-style-carousel-next]');
      this.stitchingStyleZoom = root.querySelector('[data-cs-cd-stitching-style-zoom]');
      this.stitchingStyleZoomPreview = root.querySelector('[data-cs-cd-stitching-style-zoom-preview]');
      this.stitchingStyleZoomCaption = root.querySelector('[data-cs-cd-stitching-style-zoom-caption]');
      this.stitchingStyleZoomClose = root.querySelector('[data-cs-cd-stitching-style-zoom-close]');
      this.stitchingStyleZoomTrigger = null;
      this.tipShapeCurrent = root.querySelector('[data-cs-cd-tip-shape-current]');
      this.tipShapeCarousel = root.querySelector('[data-cs-cd-option-grid="tipShape"]');
      this.tipShapeCarouselPrevious = root.querySelector('[data-cs-cd-tip-shape-carousel-previous]');
      this.tipShapeCarouselNext = root.querySelector('[data-cs-cd-tip-shape-carousel-next]');
      this.tipShapeZoom = root.querySelector('[data-cs-cd-tip-shape-zoom]');
      this.tipShapeZoomPreview = root.querySelector('[data-cs-cd-tip-shape-zoom-preview]');
      this.tipShapeZoomCaption = root.querySelector('[data-cs-cd-tip-shape-zoom-caption]');
      this.tipShapeZoomClose = root.querySelector('[data-cs-cd-tip-shape-zoom-close]');
      this.tipShapeZoomTrigger = null;
      this.threadColourZoom = root.querySelector('[data-cs-cd-thread-colour-zoom]');
      this.threadColourZoomPreview = root.querySelector('[data-cs-cd-thread-colour-zoom-preview]');
      this.threadColourZoomCaption = root.querySelector('[data-cs-cd-thread-colour-zoom-caption]');
      this.threadColourZoomClose = root.querySelector('[data-cs-cd-thread-colour-zoom-close]');
      this.threadColourZoomTrigger = null;
      this.edgeFinishingCurrent = root.querySelector('[data-cs-cd-edge-finishing-current]');
      this.cartProperties = {};

      this.tabs = Array.from(root.querySelectorAll('[data-cs-cd-tab]'));
      this.tabsContainer = root.querySelector('[data-cs-cd-tabs]');
      this.tabPanelsHost = root.querySelector('[data-cs-cd-tab-panels]');
      this.previousButton = root.querySelector('[data-cs-cd-tab-previous]');
      this.nextButton = root.querySelector('[data-cs-cd-tab-next]');
      this.tabStatus = root.querySelector('[data-cs-cd-tab-status]');
      this.activeTabId = GROUP_DEFINITIONS[0].id;
      this.groupPanels = new Map();
      this.confirmedGroups = new Set();
      this.attemptedGroups = new Set();
      root.cernirCustomStrap = this;

      this.renderCanonicalOptionGroups();
      this.setupTabNavigation();
      this.bindStyleCarousel();
      this.bindStyleZoom();
      this.bindStitchingStyleCarousel();
      this.bindStitchingStyleZoom();
      this.bindTipShapeCarousel();
      this.bindTipShapeZoom();
      this.bindThreadColourZoom();
      this.bindStateFields();
      this.bindThreadColourMode();
      this.bindEdgeColourMode();
      this.bindCustomLengthInputs();
      this.bindLengthGuide();
      this.bindFieldGuides();
      this.bindNamedMeasurementInputs();
      this.bindCustomThicknessInputs();
      this.bindAdditionalCustomisationControls();
      this.bindSummaryToggle();
      this.bindOrder();
      this.renderState();
      this.activateTab(this.activeTabId, { focus: false });
      // Arm tracking only after setup, so initial state and the first tab never count as visitor actions.
      this.trackingArmed = true;
    }

    optionSurchargeCents(field, optionId) {
      return this.commerceApi.getOptionSurchargeCents(field, optionId, {
        ...this.effectiveState,
        material: this.rawState?.material,
        [field]: optionId
      });
    }

    optionDisplayLabel(label, field, optionId) {
      const cents = this.optionSurchargeCents(field, optionId);
      return cents > 0 ? `${label} +${formatMoney(cents, this.currencyCode)}` : label;
    }

    materialDisplayLabel(material) {
      const priceCents = Number(material?.basePriceCents);
      if (!Number.isInteger(priceCents) || priceCents < 0) return material?.label || '';
      const label = String(material.label || '').replace(/\s+Leather$/i, '');
      return `${label} (${formatMoney(priceCents, this.currencyCode)})`;
    }

    setPriceModifierMetadata(element, field, optionId) {
      const cents = this.optionSurchargeCents(field, optionId);
      setData(element, 'priceModifierHook', `${field}:${optionId}`);
      setData(element, 'priceModifierCents', String(cents));
      setData(element, 'priceModifier', String(cents / 100));
    }

    connectFieldError(input) {
      const field = input?.dataset?.csCdFocusTarget;
      if (!field) return;
      const error = this.root.querySelector(`[data-cs-cd-field-error="${field}"]`);
      if (!error) return;
      if (!error.id) error.id = `cs-cd-${this.root.dataset.sectionId || 'section'}-${field}-error`;
      const describedBy = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(error.id);
      input.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      input.setAttribute('aria-errormessage', error.id);
    }

    focusField(field) {
      const target = this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`)[0];
      if (!target || target.disabled || target.closest?.('[hidden]')) return false;
      target.focus({ preventScroll: true });
      target.scrollIntoView?.({ block: 'center', inline: 'nearest' });
      return true;
    }

    bindStateFields() {
      this.root.querySelectorAll('[data-cs-cd-state-field]').forEach((input) => {
        this.bindStateInput(input);
      });
    }

    bindStateInput(input) {
      if (input.dataset.csCdStateBound === 'true') return;
      input.dataset.csCdStateBound = 'true';
      this.connectFieldError(input);
      if (['customLugWidthMm', 'customBuckleWidthMm', 'wristLengthMm', 'watchLugToLugMm'].includes(input.dataset.csCdStateField)) {
        input.addEventListener('input', () => {
          this.setRawValue(input.dataset.csCdStateField, inputValue(input), { deferRender: true });
          if (this.dimensionValidationAttempted) this.validateDimensions();
        });
        input.addEventListener('blur', () => this.validateDimensions());
      }

      input.addEventListener('change', () => {
        if (input.type === 'radio' && !input.checked) return;
        if (input.dataset.csCdStateField === 'edgeColour' && input.value !== 'on-tone') this.customEdgeColourDraft = input.value;
        this.setRawValue(input.dataset.csCdStateField, inputValue(input));
      });

      if (input.type === 'file' && input.dataset.csCdStateField === 'buckleUpload') {
        input.addEventListener('change', () => this.validateBuckle());
      }

      if (input.type === 'file' && input.dataset.csCdStateField === 'relatedImages') {
        input.addEventListener('change', () => this.validateAdditionalCustomisation());
      }

      if (input.dataset.csCdStateField === 'personalisation') {
        // State holds the stamped form (accents dropped). The box itself is only rewritten once the customer leaves
        // it, so phone keyboards that compose accented letters are never interrupted mid-word.
        const stampedForm = (value) => (value == null ? value : (this.additionalCustomisationDataApi?.normalisePersonalisation?.(value) ?? value));
        input.addEventListener('input', () => {
          this.setRawValue('personalisation', stampedForm(inputValue(input)), { deferRender: true });
          this.validateAdditionalCustomisation();
        });
        input.addEventListener('blur', () => {
          const value = typeof this.rawState.personalisation === 'string' ? this.rawState.personalisation : '';
          if (input.value !== value) input.value = value;
        });
      }

      if (input.dataset.csCdStateField === 'notes') {
        input.addEventListener('input', () => this.setRawValue('notes', inputValue(input), { deferRender: true }));
      }

      if (input.type === 'radio' && ['style', 'curvedEnd', 'materialColour', 'liningMaterial', 'liningColour', 'keeper1Colour', 'keeper2Colour', 'lugWidth', 'buckleWidth', 'lengthPreset', 'padding', 'thickness', 'foldingThickness', 'stitchingStyle', 'threadColour', 'tipShape', 'edgeFinishing', 'edgeColour', 'springBars', 'buckle'].includes(input.dataset.csCdStateField)) {
        input.addEventListener('keydown', (event) => this.onOptionKeydown(event));
      }
    }

    bindCustomLengthInputs() {
      Object.entries(this.customLengthInputs).forEach(([component, input]) => {
        if (!input || input.dataset.csCdStateBound === 'true') return;
        input.dataset.csCdStateBound = 'true';
        const update = () => {
          this.setRawValue('customLength', { ...(this.rawState.customLength || {}), [component]: input.value || null }, { deferRender: true });
          if (this.dimensionValidationAttempted) this.validateDimensions();
        };
        input.addEventListener('input', update);
        input.addEventListener('blur', () => this.validateDimensions());
      });
    }

    bindLengthGuide() {
      if (!this.lengthGuide || !this.lengthGuideOpenButton) return;
      this.lengthGuideOpenButton.addEventListener('click', () => this.openLengthGuide(this.lengthGuideOpenButton));
      this.lengthGuide.querySelectorAll('[data-cs-cd-length-guide-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeLengthGuide(true));
      });
      this.lengthGuide.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeLengthGuide(true);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.lengthGuide.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    bindFieldGuides() {
      if (!this.fieldGuide || !this.fieldGuideTitle || !this.fieldGuideBody) return;
      this.fieldGuideOpenButtons.forEach((button) => {
        button.addEventListener('click', () => this.openFieldGuide(button.dataset.csCdFieldGuideOpen, button));
      });
      this.fieldGuide.querySelectorAll('[data-cs-cd-field-guide-close]').forEach((control) => {
        control.addEventListener('click', () => this.closeFieldGuide(true));
      });
      this.fieldGuide.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeFieldGuide(true);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.fieldGuide.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex="0"]'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    openFieldGuide(key, trigger) {
      if (!key || !this.fieldGuide || !this.fieldGuideTitle || !this.fieldGuideBody) return;
      const template = this.root.querySelector(`[data-cs-cd-field-guide-content="${key}"]`);
      if (!template?.content) return;
      this.fieldGuideTrigger?.setAttribute('aria-expanded', 'false');
      this.fieldGuideTrigger = trigger;
      this.fieldGuideTrigger?.setAttribute('aria-expanded', 'true');
      this.fieldGuideTitle.textContent = template.dataset.guideTitle || 'Option guide';
      this.fieldGuideBody.replaceChildren(template.content.cloneNode(true));
      this.fieldGuide.hidden = false;
      this.fieldGuide.setAttribute('aria-hidden', 'false');
      this.fieldGuide.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.fieldGuide.querySelector('.cs-cd__field-guide-close')?.focus();
    }

    closeFieldGuide(restoreFocus = true) {
      if (!this.fieldGuide) return;
      this.fieldGuide.classList.remove('is-open');
      this.fieldGuide.hidden = true;
      this.fieldGuide.setAttribute('aria-hidden', 'true');
      this.fieldGuideTrigger?.setAttribute('aria-expanded', 'false');
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      if (restoreFocus) this.fieldGuideTrigger?.focus?.();
      this.fieldGuideTrigger = null;
    }

    openLengthGuide(trigger) {
      if (!this.lengthGuide) return;
      this.lengthGuideTrigger = trigger || this.lengthGuideOpenButton;
      this.lengthGuide.hidden = false;
      this.lengthGuide.setAttribute('aria-hidden', 'false');
      this.lengthGuide.classList.add('is-open');
      this.lengthGuideOpenButton?.setAttribute('aria-expanded', 'true');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      const activateCalculator = () => window.CERNIR?.StrapSizing?.init?.(this.lengthGuide);
      if (window.CERNIR?.StrapSizing) activateCalculator();
      else window.CernirFeatureLoader?.loadStrapSizing?.(this.lengthGuide).then(activateCalculator).catch(() => {});
      this.lengthGuide.querySelector('.cs-cd__length-guide-close')?.focus();
    }

    closeLengthGuide(restoreFocus = true) {
      if (!this.lengthGuide) return;
      this.lengthGuide.classList.remove('is-open');
      this.lengthGuide.hidden = true;
      this.lengthGuide.setAttribute('aria-hidden', 'true');
      this.lengthGuideOpenButton?.setAttribute('aria-expanded', 'false');
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      if (restoreFocus) this.lengthGuideTrigger?.focus?.();
      this.lengthGuideTrigger = null;
    }

    applyLengthRecommendation(result) {
      result = result || {};
      const longEndMm = Number(result.longMm);
      const shortEndMm = Number(result.shortMm);
      const route = this.dimensionsDataApi?.getLengthRouteId(this.rawState.style);
      const options = this.dimensionsDataApi?.getLengthOptions(this.rawState.style) || [];
      const lengthSelect = this.root.querySelector('[data-cs-cd-dimension-options="lengthPreset"]');
      if (!['standard', 'custom'].includes(result.status) || !Number.isInteger(longEndMm) || !Number.isInteger(shortEndMm) || longEndMm <= 0 || shortEndMm <= 0) {
        return { ok: false, reason: 'invalid_recommendation' };
      }
      if (!lengthSelect || route === 'wristBased') return { ok: false, reason: 'size_unavailable' };

      const exactOption = options.find((option) => option.id !== 'other' && !option.disabled && option.longEndMm === longEndMm && option.shortEndMm === shortEndMm);
      if (result.status === 'standard') {
        if (!exactOption) return { ok: false, reason: 'size_unavailable' };
        this.setRawValue('lengthPreset', exactOption.id);
        if (this.dimensionValidationAttempted) this.validateDimensions();
        return { ok: true, control: lengthSelect, value: exactOption.id };
      }

      const customOption = options.find((option) => option.id === 'other' && !option.disabled);
      if (!customOption || !this.customLengthInputs.longEndMm || !this.customLengthInputs.shortEndMm) {
        return { ok: false, reason: 'custom_length_unavailable' };
      }
      this.rawState = this.stateApi.updateRawState(this.rawState, 'customLength', {
        longEndMm: String(longEndMm),
        shortEndMm: String(shortEndMm)
      });
      this.setRawValue('lengthPreset', 'other');
      if (this.dimensionValidationAttempted) this.validateDimensions();
      return { ok: true, control: this.customLengthInputs.longEndMm, value: `${longEndMm}/${shortEndMm} mm` };
    }

    bindThreadColourMode() {
      if (!this.threadColourModeSelect) return;
      this.connectFieldError(this.threadColourModeSelect);
      this.threadColourModeSelect.addEventListener('change', () => {
        this.threadColourMode = this.threadColourModeSelect.value;
        this.setRawValue('threadColour', this.threadColourMode === 'choose-another-colour' ? null : this.threadColourMode);
        if (this.stitchingValidationAttempted) this.validateStitching();
      });
    }

    bindEdgeColourMode() {
      if (!this.edgeColourModeSelect) return;
      this.connectFieldError(this.edgeColourModeSelect);
      this.edgeColourModeSelect.addEventListener('change', () => {
        this.edgeColourMode = this.edgeColourModeSelect.value;
        if (this.edgeColourMode === 'on-tone' && this.rawState.edgeColour && this.rawState.edgeColour !== 'on-tone') {
          this.customEdgeColourDraft = this.rawState.edgeColour;
        }
        this.setRawValue('edgeColour', this.edgeColourMode === 'on-tone' ? 'on-tone' : this.customEdgeColourDraft);
        if (this.finishingHardwareValidationAttempted) this.validateFinishingHardware();
      });
    }

    bindNamedMeasurementInputs() {
      this.namedMeasurementInputs.forEach((input) => {
        if (input.dataset.csCdStateBound === 'true') return;
        input.dataset.csCdStateBound = 'true';
        const field = input.dataset.csCdNamedMeasurementField;
        const component = input.dataset.csCdNamedMeasurementComponent;
        const update = () => {
          this.setRawValue(field, { ...(this.rawState[field] || {}), [component]: input.value || null }, { deferRender: true });
          if (this.dimensionValidationAttempted) this.validateDimensions();
        };
        input.addEventListener('input', update);
        input.addEventListener('blur', () => this.validateDimensions());
      });
    }

    bindCustomThicknessInputs() {
      Object.entries(this.customThicknessInputs).forEach(([component, input]) => {
        if (!input || input.dataset.csCdStateBound === 'true') return;
        input.dataset.csCdStateBound = 'true';
        const update = () => {
          this.setRawValue('customThickness', { ...(this.rawState.customThickness || {}), [component]: input.value || null }, { deferRender: true });
          if (this.paddingThicknessValidationAttempted) this.validatePaddingThickness();
        };
        input.addEventListener('input', update);
        input.addEventListener('blur', () => this.validatePaddingThickness());
      });
    }

    bindAdditionalCustomisationControls() {
      this.root.querySelector('[data-cs-cd-related-images-clear]')?.addEventListener('click', () => {
        this.clearRelatedImagesInput();
        this.setRawValue('relatedImages', []);
        this.validateAdditionalCustomisation();
      });
    }

    bindOrder() {
      this.orderForm?.addEventListener('submit', (event) => this.submitOrder(event));
    }

    bindSummaryToggle() {
      if (!this.summaryToggle || !this.summaryBody) return;
      this.summaryToggle.addEventListener('click', () => {
        this.setSummaryExpanded(this.summaryToggle.getAttribute('aria-expanded') !== 'true');
      });
      this.setSummaryExpanded(this.summaryToggle.getAttribute('aria-expanded') === 'true');
    }

    setSummaryExpanded(expanded) {
      if (!this.summaryToggle || !this.summaryBody) return;
      this.summaryToggle.setAttribute('aria-expanded', String(expanded));
      this.summaryBody.hidden = !expanded;
      if (this.summaryToggleLabel) this.summaryToggleLabel.textContent = expanded ? 'Hide summary' : 'Show summary';
    }

    clearRelatedImagesInput() {
      if (this.relatedImagesInput) this.relatedImagesInput.value = '';
    }

    normalizeKey(value) {
      return String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    readShopifySwatches() {
      const entries = this.root.querySelector('[data-cs-cd-shopify-material-swatches]')?.content?.querySelectorAll('[data-material-key]') || [];
      const index = new Map();
      Array.from(entries).forEach((entry) => {
        const materialKeys = [entry.dataset.materialKey, entry.dataset.materialName].filter(Boolean);
        materialKeys.forEach((materialKey) => {
          index.set(`${this.normalizeKey(materialKey)}:${this.normalizeKey(entry.dataset.colourKey)}`, {
            imageUrl: entry.dataset.imageUrl || '',
            originalImageUrl: entry.dataset.originalImageUrl || entry.dataset.imageUrl || '',
            fallbackHex: entry.dataset.fallbackHex || ''
          });
        });
      });
      return index;
    }

    getShopifySwatch(material, colour) {
      const materialKeys = [
        material.id,
        ...(MATERIAL_SWATCH_ALIASES[material.id] || []),
        this.normalizeKey(material.label),
        this.normalizeKey(material.label.split('/')[0])
      ];
      for (const materialKey of materialKeys) {
        const swatch = this.shopifySwatches.get(`${materialKey}:${colour.id}`) || this.shopifySwatches.get(`${materialKey}:${this.normalizeKey(colour.label)}`);
        if (swatch) return swatch;
      }
      return null;
    }

    // Colour swatches are at most ~88 CSS px wide at every breakpoint (measured 66–87 px), but the data carries the
    // 320 px Shopify image. Offer 160/240/320 widths of the same file so 1x–2x screens skip the large one;
    // 3x screens still pick 320.
    setSwatchImageSource(image, url) {
      image.src = url;
      if (!/[?&]width=\d+/.test(url)) return;
      image.srcset = [160, 240, 320].map((width) => `${url.replace(/([?&]width=)\d+/, `$1${width}`)} ${width}w`).join(', ');
      image.sizes = '88px';
    }

    createMaterialColourCard(material, colour) {
      const swatch = this.getShopifySwatch(material, colour);
      const label = document.createElement('label');
      label.className = 'cs-cd__material-colour-card';
      label.dataset.colourId = colour.id;
      label.dataset.visualSource = swatch?.imageUrl ? 'shopify-metaobject-image' : (swatch?.fallbackHex ? 'shopify-metaobject-hex' : 'placeholder');
      label.dataset.previewColour = swatch?.fallbackHex || '';
      label.dataset.previewSwatchUrl = swatch?.originalImageUrl || swatch?.imageUrl || '';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-material-colour-${this.root.dataset.sectionId || 'section'}`;
      input.value = colour.id;
      input.dataset.csCdStateField = 'materialColour';
      input.dataset.csCdFocusTarget = 'materialColour';
      input.checked = colour.id === this.rawState.materialColour;

      const visual = document.createElement('span');
      visual.className = 'cs-cd__material-colour-visual';
      visual.setAttribute('aria-hidden', 'true');
      if (swatch?.imageUrl) {
        const image = document.createElement('img');
        this.setSwatchImageSource(image, swatch.imageUrl);
        image.alt = '';
        image.loading = 'lazy';
        visual.append(image);
      } else {
        visual.dataset.placeholderAsset = `${material.id}:${colour.id}`;
        if (swatch?.fallbackHex) visual.style.backgroundColor = swatch.fallbackHex;
      }

      const title = document.createElement('span');
      title.className = 'cs-cd__material-colour-name';
      title.textContent = colour.label;
      label.append(input, visual, title);
      this.bindStateInput(input);
      const item = document.createElement('div');
      item.className = 'cs-cd__material-colour-item';
      item.append(label);
      if (swatch?.imageUrl) item.append(this.createColourZoomButton(material, colour, swatch));
      return item;
    }

    createKeeperColourCard(material, colour, field, matchMain) {
      const choiceId = matchMain ? 'match-main' : colour.id;
      const swatch = this.getShopifySwatch(material, colour);
      const label = document.createElement('label');
      label.className = 'cs-cd__material-colour-card cs-cd__keeper-colour-card';
      label.dataset.colourId = choiceId;
      label.dataset.visualSource = swatch?.imageUrl ? 'shopify-metaobject-image' : (swatch?.fallbackHex ? 'shopify-metaobject-hex' : 'placeholder');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-${field}-${this.root.dataset.sectionId || 'section'}`;
      input.value = choiceId;
      input.required = true;
      input.checked = choiceId === this.rawState[field];
      input.dataset.csCdStateField = field;
      input.dataset.csCdFocusTarget = field;
      input.setAttribute('aria-label', matchMain ? `Match main leather — ${colour.label}` : colour.label);

      const visual = document.createElement('span');
      visual.className = 'cs-cd__material-colour-visual';
      visual.setAttribute('aria-hidden', 'true');
      if (swatch?.imageUrl) {
        const image = document.createElement('img');
        this.setSwatchImageSource(image, swatch.imageUrl);
        image.alt = '';
        image.loading = 'lazy';
        visual.append(image);
      } else {
        visual.dataset.placeholderAsset = `${material.id}:${colour.id}`;
        if (swatch?.fallbackHex) visual.style.backgroundColor = swatch.fallbackHex;
      }

      const title = document.createElement('span');
      title.className = 'cs-cd__material-colour-name';
      title.textContent = matchMain ? 'Match leather' : colour.label;
      label.append(input, visual, title);
      this.bindStateInput(input);
      const item = document.createElement('div');
      item.className = 'cs-cd__material-colour-item';
      item.append(label);
      if (swatch?.imageUrl) item.append(this.createColourZoomButton(material, colour, swatch));
      return item;
    }

    createColourZoomButton(material, colour, swatch) {
      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button cs-cd__material-colour-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${material.label} ${colour.label} preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openMaterialColourZoom(material, colour, swatch, zoomButton);
      });
      return zoomButton;
    }

    openMaterialColourZoom(material, colour, swatch, trigger) {
      if (!this.styleZoom || !this.styleZoomPreview) return;
      let preview;
      if (swatch?.imageUrl) {
        preview = document.createElement('img');
        preview.className = 'cs-cd__material-colour-zoom-image cs-cd__compact-zoom-image';
        preview.src = swatch.originalImageUrl || swatch.imageUrl;
        preview.alt = `${material.label} ${colour.label}`;
        preview.decoding = 'async';
      } else {
        preview = document.createElement('span');
        preview.className = 'cs-cd__material-colour-visual cs-cd__material-colour-zoom-visual cs-cd__compact-zoom-visual';
        preview.setAttribute('role', 'img');
        preview.setAttribute('aria-label', `${material.label} ${colour.label}`);
        preview.dataset.placeholderAsset = `${material.id}:${colour.id}`;
        if (swatch?.fallbackHex) preview.style.backgroundColor = swatch.fallbackHex;
      }
      this.styleZoomPreview.replaceChildren(preview);
      this.styleZoom.dataset.compactZoom = 'true';
      this.styleZoomPreview.dataset.compactZoom = swatch?.imageUrl ? 'image' : 'visual';
      this.styleZoom.dataset.previewKind = 'material-colour';
      this.styleZoomPreview.dataset.previewKind = 'material-colour';
      this.mountZoomPortal(this.styleZoom);
      if (this.styleZoomCaption) this.styleZoomCaption.textContent = `${material.label} ${colour.label}`;
      this.styleZoomTrigger = trigger;
      this.styleZoom.hidden = false;
      this.styleZoom.setAttribute('aria-hidden', 'false');
      this.styleZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.styleZoomClose?.focus();
    }

    createLiningColourCard(material, colour) {
      const swatch = this.getShopifySwatch(material, colour);
      const label = document.createElement('label');
      label.className = 'cs-cd__material-colour-card';
      label.dataset.colourId = colour.id;
      label.dataset.visualSource = swatch?.imageUrl ? 'shopify-metaobject-image' : (swatch?.fallbackHex ? 'shopify-metaobject-hex' : 'placeholder');
      label.dataset.priceModifierHook = `lining-colour:${material.id}:${colour.id}`;
      label.dataset.priceModifier = colour.priceModifier == null ? '' : String(colour.priceModifier);
      if (colour.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-lining-colour-${this.root.dataset.sectionId || 'section'}`;
      input.value = colour.id;
      input.disabled = Boolean(colour.disabled);
      input.required = true;
      input.checked = colour.id === this.rawState.liningColour;
      input.dataset.csCdStateField = 'liningColour';
      input.dataset.csCdFocusTarget = 'liningColour';
      input.setAttribute('aria-label', colour.helperText);

      const visual = document.createElement('span');
      visual.className = 'cs-cd__material-colour-visual';
      visual.setAttribute('aria-hidden', 'true');
      if (swatch?.imageUrl) {
        const image = document.createElement('img');
        this.setSwatchImageSource(image, swatch.imageUrl);
        image.alt = '';
        image.loading = 'lazy';
        visual.append(image);
      } else {
        visual.dataset.placeholderAsset = `lining:${material.id}:${colour.id}`;
        if (swatch?.fallbackHex) visual.style.backgroundColor = swatch.fallbackHex;
      }
      const title = document.createElement('span');
      title.className = 'cs-cd__material-colour-name';
      title.textContent = colour.label;
      label.append(input, visual, title);
      this.bindStateInput(input);
      const item = document.createElement('div');
      item.className = 'cs-cd__material-colour-item';
      item.append(label, this.createColourZoomButton(material, colour, swatch));
      return item;
    }

    appendOptionImage(thumbnail, group, option, fallbackParts) {
      const imageUrl = this.optionImageAssets.get(`${group}:${option.id}`);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        thumbnail.dataset.thumbnailKind = 'image';
        thumbnail.append(image);
        return;
      }
      fallbackParts.forEach((part) => thumbnail.append(part));
    }

    createStyleThumbnail(option, field = 'style') {
      const thumbnail = document.createElement('span');
      thumbnail.className = 'cs-cd__style-thumbnail';
      thumbnail.dataset.thumbnailKind = option.thumbnail.kind;
      thumbnail.dataset.thumbnailVariant = option.thumbnail.variant;
      thumbnail.setAttribute('aria-hidden', 'true');
      this.appendOptionImage(thumbnail, field, option, [document.createElement('i'), document.createElement('i')]);
      return thumbnail;
    }

    createOptionCard(field, option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__style-card';
      label.dataset.optionId = option.id;
      this.setPriceModifierMetadata(label, field, option.id);
      if (option.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-${field}-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = field;
      if (field === 'style' || field === 'curvedEnd') input.dataset.csCdFocusTarget = field;
      const helperId = `cs-cd-${this.root.dataset.sectionId || 'section'}-${field}-${option.id}-helper`;
      input.setAttribute('aria-describedby', helperId);

      const content = document.createElement('span');
      content.className = 'cs-cd__style-card-content';

      const thumbnail = this.createStyleThumbnail(option, field);

      const copy = document.createElement('span');
      copy.className = 'cs-cd__style-card-copy';
      const title = document.createElement('strong');
      title.textContent = this.optionDisplayLabel(option.title, field, option.id);
      const helper = document.createElement('small');
      helper.id = helperId;
      helper.textContent = option.helperText;
      if (field === 'style' || field === 'curvedEnd') helper.className = 'cs-cd__card-hint';
      copy.append(title, helper);
      content.append(thumbnail, copy);
      label.append(input, content);
      if (field !== 'style') return label;

      const item = document.createElement('div');
      item.className = 'cs-cd__style-item';
      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${option.title} style preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openStyleZoom(option, zoomButton);
      });
      item.append(label, zoomButton);
      return item;
    }

    bindStyleCarousel() {
      if (!this.styleCarousel) return;
      const scroll = (direction) => {
        const distance = Math.max(this.styleCarousel.clientWidth * 0.8, 120);
        if (typeof this.styleCarousel.scrollBy === 'function') {
          this.styleCarousel.scrollBy({ left: direction * distance, behavior: 'smooth' });
        } else {
          this.styleCarousel.scrollLeft += direction * distance;
        }
      };

      this.styleCarouselPrevious?.addEventListener('click', () => scroll(-1));
      this.styleCarouselNext?.addEventListener('click', () => scroll(1));
      this.styleCarousel.addEventListener('keydown', (event) => {
        if (event.target !== this.styleCarousel || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        scroll(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const updateControls = () => this.updateStyleCarouselControls();
      this.styleCarousel.addEventListener('scroll', updateControls, { passive: true });
      if (typeof window.addEventListener === 'function') window.addEventListener('resize', updateControls, { passive: true });
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(updateControls);
      else updateControls();
    }

    updateStyleCarouselControls() {
      if (!this.styleCarousel) return;
      const maxScroll = Math.max(0, this.styleCarousel.scrollWidth - this.styleCarousel.clientWidth);
      if (this.styleCarouselPrevious) this.styleCarouselPrevious.disabled = this.styleCarousel.scrollLeft <= 4;
      if (this.styleCarouselNext) this.styleCarouselNext.disabled = this.styleCarousel.scrollLeft >= maxScroll - 4;
    }

    bindStyleZoom() {
      if (!this.styleZoom) return;
      this.styleZoomClose?.addEventListener('click', () => this.closeStyleZoom());
      this.styleZoom.addEventListener('click', (event) => {
        if (event.target === this.styleZoom) this.closeStyleZoom();
      });
      this.styleZoom.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeStyleZoom();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.styleZoomClose?.focus();
        }
      });
    }

    mountZoomPortal(modal) {
      if (!modal || !document.body || this.zoomPortals.has(modal)) return;
      const host = document.createElement('div');
      host.className = 'cs-cd cs-cd__zoom-portal-host';
      host.setAttribute('data-cs-cd-zoom-portal', '');
      const home = { parent: modal.parentNode, next: modal.nextSibling, host };
      document.body.append(host);
      host.append(modal);
      this.zoomPortals.set(modal, home);
    }

    restoreZoomHome(modal) {
      const home = modal ? this.zoomPortals.get(modal) : null;
      if (!modal || !home) return;
      const { parent, next, host } = home;
      parent?.insertBefore(modal, next?.parentNode === parent ? next : null);
      host.remove();
      this.zoomPortals.delete(modal);
    }


    openStyleZoom(option, trigger) {
      if (!this.styleZoom || !this.styleZoomPreview) return;
      const thumbnail = this.createStyleThumbnail(option);
      const image = thumbnail.querySelector('img');
      const preview = image || thumbnail;
      if (image) {
        image.src = this.optionFullImageAssets.get(`style:${option.id}`) || image.src;
        image.className = 'cs-cd__style-zoom-image cs-cd__compact-zoom-image';
        image.alt = `${option.title} strap style illustration`;
        image.loading = 'eager';
      } else {
        preview.removeAttribute('aria-hidden');
        preview.setAttribute('role', 'img');
        preview.setAttribute('aria-label', `${option.title} strap style illustration`);
      }
      this.styleZoomPreview.replaceChildren(preview);
      this.styleZoom.dataset.compactZoom = 'true';
      this.styleZoomPreview.dataset.compactZoom = image ? 'image' : 'visual';
      this.styleZoom.dataset.previewKind = 'style';
      this.styleZoomPreview.dataset.previewKind = 'style';
      this.mountZoomPortal(this.styleZoom);
      if (this.styleZoomCaption) this.styleZoomCaption.textContent = option.title;
      this.styleZoomTrigger = trigger;
      this.styleZoom.hidden = false;
      this.styleZoom.setAttribute('aria-hidden', 'false');
      this.styleZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.styleZoomClose?.focus();
    }

    openPaddingZoom(option, trigger) {
      if (!this.styleZoom || !this.styleZoomPreview) return;
      const thumbnail = this.createPaddingThumbnail(option, true);
      const image = thumbnail.querySelector('img');
      const preview = image || thumbnail;
      if (image) {
        image.src = this.optionFullImageAssets.get(`padding:${option.id}`) || image.src;
        image.className = 'cs-cd__compact-zoom-image';
      }
      else preview.classList.add('cs-cd__compact-zoom-visual');
      preview.removeAttribute('aria-hidden');
      preview.setAttribute('role', 'img');
      preview.setAttribute('aria-label', `${option.label} watch strap padding illustration`);
      this.styleZoomPreview.replaceChildren(preview);
      this.styleZoom.dataset.compactZoom = 'true';
      this.styleZoomPreview.dataset.compactZoom = image ? 'image' : 'visual';
      this.styleZoom.dataset.previewKind = 'padding';
      this.styleZoomPreview.dataset.previewKind = 'padding';
      this.mountZoomPortal(this.styleZoom);
      if (this.styleZoomCaption) this.styleZoomCaption.textContent = `Padded: ${option.label}`;
      this.styleZoomTrigger = trigger;
      this.styleZoom.hidden = false;
      this.styleZoom.setAttribute('aria-hidden', 'false');
      this.styleZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.styleZoomClose?.focus();
    }

    closeStyleZoom() {
      if (!this.styleZoom || this.styleZoom.hidden) return;
      this.styleZoom.classList.remove('is-open');
      this.styleZoom.hidden = true;
      this.styleZoom.setAttribute('aria-hidden', 'true');
      delete this.styleZoom.dataset.previewKind;
      delete this.styleZoom.dataset.compactZoom;
      delete this.styleZoomPreview?.dataset.previewKind;
      delete this.styleZoomPreview?.dataset.compactZoom;
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      this.styleZoomTrigger?.focus();
      this.styleZoomTrigger = null;
      this.restoreZoomHome(this.styleZoom);
    }

    bindStitchingStyleCarousel() {
      if (!this.stitchingStyleCarousel) return;
      const scroll = (direction) => {
        const distance = Math.max(this.stitchingStyleCarousel.clientWidth * 0.8, 120);
        if (typeof this.stitchingStyleCarousel.scrollBy === 'function') {
          this.stitchingStyleCarousel.scrollBy({ left: direction * distance, behavior: 'smooth' });
        } else {
          this.stitchingStyleCarousel.scrollLeft += direction * distance;
        }
      };

      this.stitchingStyleCarouselPrevious?.addEventListener('click', () => scroll(-1));
      this.stitchingStyleCarouselNext?.addEventListener('click', () => scroll(1));
      this.stitchingStyleCarousel.addEventListener('keydown', (event) => {
        if (event.target !== this.stitchingStyleCarousel || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        scroll(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const updateControls = () => this.updateStitchingStyleCarouselControls();
      this.stitchingStyleCarousel.addEventListener('scroll', updateControls, { passive: true });
      if (typeof window.addEventListener === 'function') window.addEventListener('resize', updateControls, { passive: true });
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(updateControls);
      else updateControls();
    }

    updateStitchingStyleCarouselControls() {
      if (!this.stitchingStyleCarousel) return;
      const maxScroll = Math.max(0, this.stitchingStyleCarousel.scrollWidth - this.stitchingStyleCarousel.clientWidth);
      if (this.stitchingStyleCarouselPrevious) this.stitchingStyleCarouselPrevious.disabled = this.stitchingStyleCarousel.scrollLeft <= 4;
      if (this.stitchingStyleCarouselNext) this.stitchingStyleCarouselNext.disabled = this.stitchingStyleCarousel.scrollLeft >= maxScroll - 4;
    }

    bindStitchingStyleZoom() {
      if (!this.stitchingStyleZoom) return;
      this.stitchingStyleZoomClose?.addEventListener('click', () => this.closeStitchingStyleZoom());
      this.stitchingStyleZoom.addEventListener('click', (event) => {
        if (event.target === this.stitchingStyleZoom) this.closeStitchingStyleZoom();
      });
      this.stitchingStyleZoom.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeStitchingStyleZoom();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.stitchingStyleZoomClose?.focus();
        }
      });
    }

    openStitchingStyleZoom(option, trigger) {
      if (!this.stitchingStyleZoom || !this.stitchingStyleZoomPreview) return;
      const thumbnail = this.createStitchingStyleThumbnail(option);
      const image = thumbnail.querySelector('img');
      const preview = image || thumbnail;
      if (image) {
        image.src = this.optionFullImageAssets.get(`stitchingStyle:${option.id}`) || image.src;
        image.className = 'cs-cd__compact-zoom-image';
      }
      else preview.classList.add('cs-cd__compact-zoom-visual');
      preview.removeAttribute('aria-hidden');
      preview.setAttribute('role', 'img');
      preview.setAttribute('aria-label', `${option.title} stitching style illustration`);
      this.stitchingStyleZoomPreview.replaceChildren(preview);
      this.stitchingStyleZoom.dataset.compactZoom = 'true';
      this.stitchingStyleZoomPreview.dataset.compactZoom = image ? 'image' : 'visual';
      this.mountZoomPortal(this.stitchingStyleZoom);
      if (this.stitchingStyleZoomCaption) this.stitchingStyleZoomCaption.textContent = option.title;
      this.stitchingStyleZoomTrigger = trigger;
      this.stitchingStyleZoom.hidden = false;
      this.stitchingStyleZoom.setAttribute('aria-hidden', 'false');
      this.stitchingStyleZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.stitchingStyleZoomClose?.focus();
    }

    closeStitchingStyleZoom() {
      if (!this.stitchingStyleZoom || this.stitchingStyleZoom.hidden) return;
      this.stitchingStyleZoom.classList.remove('is-open');
      this.stitchingStyleZoom.hidden = true;
      this.stitchingStyleZoom.setAttribute('aria-hidden', 'true');
      delete this.stitchingStyleZoom.dataset.compactZoom;
      delete this.stitchingStyleZoomPreview?.dataset.compactZoom;
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      this.stitchingStyleZoomTrigger?.focus();
      this.stitchingStyleZoomTrigger = null;
      this.restoreZoomHome(this.stitchingStyleZoom);
    }

    bindTipShapeCarousel() {
      if (!this.tipShapeCarousel) return;
      const scroll = (direction) => {
        const distance = Math.max(this.tipShapeCarousel.clientWidth * 0.8, 120);
        if (typeof this.tipShapeCarousel.scrollBy === 'function') {
          this.tipShapeCarousel.scrollBy({ left: direction * distance, behavior: 'smooth' });
        } else {
          this.tipShapeCarousel.scrollLeft += direction * distance;
        }
      };

      this.tipShapeCarouselPrevious?.addEventListener('click', () => scroll(-1));
      this.tipShapeCarouselNext?.addEventListener('click', () => scroll(1));
      this.tipShapeCarousel.addEventListener('keydown', (event) => {
        if (event.target !== this.tipShapeCarousel || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        scroll(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const updateControls = () => this.updateTipShapeCarouselControls();
      this.tipShapeCarousel.addEventListener('scroll', updateControls, { passive: true });
      if (typeof window.addEventListener === 'function') window.addEventListener('resize', updateControls, { passive: true });
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(updateControls);
      else updateControls();
    }

    updateTipShapeCarouselControls() {
      if (!this.tipShapeCarousel) return;
      const maxScroll = Math.max(0, this.tipShapeCarousel.scrollWidth - this.tipShapeCarousel.clientWidth);
      if (this.tipShapeCarouselPrevious) this.tipShapeCarouselPrevious.disabled = this.tipShapeCarousel.scrollLeft <= 4;
      if (this.tipShapeCarouselNext) this.tipShapeCarouselNext.disabled = this.tipShapeCarousel.scrollLeft >= maxScroll - 4;
    }

    bindTipShapeZoom() {
      if (!this.tipShapeZoom) return;
      this.tipShapeZoomClose?.addEventListener('click', () => this.closeTipShapeZoom());
      this.tipShapeZoom.addEventListener('click', (event) => {
        if (event.target === this.tipShapeZoom) this.closeTipShapeZoom();
      });
      this.tipShapeZoom.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeTipShapeZoom();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.tipShapeZoomClose?.focus();
        }
      });
    }

    openTipShapeZoom(option, trigger) {
      if (!this.tipShapeZoom || !this.tipShapeZoomPreview) return;
      const thumbnail = this.createTipShapeThumbnail(option);
      const image = thumbnail.querySelector('img');
      const preview = image || thumbnail;
      if (image) image.className = 'cs-cd__compact-zoom-image';
      else preview.classList.add('cs-cd__compact-zoom-visual');
      preview.removeAttribute('aria-hidden');
      preview.setAttribute('role', 'img');
      preview.setAttribute('aria-label', `${option.title} tip shape illustration`);
      this.tipShapeZoomPreview.replaceChildren(preview);
      this.tipShapeZoom.dataset.compactZoom = 'true';
      this.tipShapeZoomPreview.dataset.compactZoom = image ? 'image' : 'visual';
      this.mountZoomPortal(this.tipShapeZoom);
      if (this.tipShapeZoomCaption) this.tipShapeZoomCaption.textContent = option.title;
      this.tipShapeZoomTrigger = trigger;
      this.tipShapeZoom.hidden = false;
      this.tipShapeZoom.setAttribute('aria-hidden', 'false');
      this.tipShapeZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.tipShapeZoomClose?.focus();
    }

    closeTipShapeZoom() {
      if (!this.tipShapeZoom || this.tipShapeZoom.hidden) return;
      this.tipShapeZoom.classList.remove('is-open');
      this.tipShapeZoom.hidden = true;
      this.tipShapeZoom.setAttribute('aria-hidden', 'true');
      delete this.tipShapeZoom.dataset.compactZoom;
      delete this.tipShapeZoomPreview?.dataset.compactZoom;
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      this.tipShapeZoomTrigger?.focus();
      this.tipShapeZoomTrigger = null;
      this.restoreZoomHome(this.tipShapeZoom);
    }

    bindThreadColourZoom() {
      if (!this.threadColourZoom) return;
      this.threadColourZoomClose?.addEventListener('click', () => this.closeThreadColourZoom());
      this.threadColourZoom.addEventListener('click', (event) => {
        if (event.target === this.threadColourZoom) this.closeThreadColourZoom();
      });
      this.threadColourZoom.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeThreadColourZoom();
        } else if (event.key === 'Tab') {
          event.preventDefault();
          this.threadColourZoomClose?.focus();
        }
      });
    }

    openThreadColourZoom(option, trigger) {
      if (!this.threadColourZoom || !this.threadColourZoomPreview) return;
      const asset = this.threadColourAssets.get(option.id);
      const image = document.createElement('img');
      image.className = 'cs-cd__thread-colour-zoom-image cs-cd__compact-zoom-image';
      image.src = asset?.fullImageUrl || asset?.imageUrl || '';
      image.alt = `${option.title} stitch colour`;
      image.decoding = 'async';
      this.threadColourZoomPreview.replaceChildren(image);
      this.threadColourZoom.dataset.compactZoom = 'true';
      this.threadColourZoomPreview.dataset.compactZoom = 'image';
      this.mountZoomPortal(this.threadColourZoom);
      if (this.threadColourZoomCaption) this.threadColourZoomCaption.textContent = option.title;
      this.threadColourZoomTrigger = trigger;
      this.threadColourZoom.hidden = false;
      this.threadColourZoom.setAttribute('aria-hidden', 'false');
      this.threadColourZoom.classList.add('is-open');
      document.documentElement?.classList?.add('cs-cd__zoom-lock');
      this.threadColourZoomClose?.focus();
    }

    closeThreadColourZoom() {
      if (!this.threadColourZoom || this.threadColourZoom.hidden) return;
      this.threadColourZoom.classList.remove('is-open');
      this.threadColourZoom.hidden = true;
      this.threadColourZoom.setAttribute('aria-hidden', 'true');
      delete this.threadColourZoom.dataset.compactZoom;
      delete this.threadColourZoomPreview?.dataset.compactZoom;
      document.documentElement?.classList?.remove('cs-cd__zoom-lock');
      this.threadColourZoomTrigger?.focus();
      this.threadColourZoomTrigger = null;
      this.restoreZoomHome(this.threadColourZoom);
    }


    createPaddingThumbnail(option, zoomed = false) {
      const thumbnail = document.createElement('span');
      thumbnail.className = `cs-cd__padding-thumbnail${zoomed ? ' cs-cd__padding-thumbnail--zoom' : ''}`;
      thumbnail.dataset.paddingVariant = option.id;
      thumbnail.setAttribute('aria-hidden', 'true');
      const fallbackParts = [];
      for (const className of ['strap', 'stitches', 'profile']) {
        const part = document.createElement('i');
        part.className = `cs-cd__padding-${className}`;
        fallbackParts.push(part);
      }
      this.appendOptionImage(thumbnail, 'padding', option, fallbackParts);
      return thumbnail;
    }

    createPaddingOption(option) {
      const item = document.createElement('div');
      item.className = 'cs-cd__padding-item';
      item.dataset.optionId = option.id;
      this.setPriceModifierMetadata(item, 'padding', option.id);
      if (option.disabled) item.dataset.disabled = 'true';

      const label = document.createElement('label');
      label.className = 'cs-cd__padding-card';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-padding-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'padding';
      input.dataset.csCdFocusTarget = 'padding';

      const content = document.createElement('span');
      content.className = 'cs-cd__padding-card-content';
      const title = document.createElement('strong');
      title.textContent = this.optionDisplayLabel(option.label, 'padding', option.id);
      content.append(this.createPaddingThumbnail(option), title);
      label.append(input, content);

      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button cs-cd__padding-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${option.label} padding preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openPaddingZoom(option, zoomButton);
      });
      item.append(label, zoomButton);
      return item;
    }

    renderDimensionSelect(select, placeholder, field, options) {
      if (!select) return;
      const selectedValue = this.rawState[field] || '';
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      placeholderOption.disabled = true;

      const optionElements = options.map((item) => {
        const option = document.createElement('option');
        option.value = item.id;
        option.textContent = item.displayLabel || item.label;
        option.disabled = Boolean(item.disabled);
        option.dataset.priceModifierHook = `${field}:${item.id}`;
        option.dataset.priceModifier = item.priceModifier == null ? '' : String(item.priceModifier);
        return option;
      });
      select.replaceChildren(placeholderOption, ...optionElements);
      select.value = selectedValue;
    }


    createStitchingStyleThumbnail(option) {
      const thumbnail = document.createElement('span');
      thumbnail.className = 'cs-cd__style-thumbnail cs-cd__stitching-style-thumbnail';
      thumbnail.dataset.thumbnailKind = option.thumbnail.kind;
      thumbnail.dataset.thumbnailVariant = option.thumbnail.variant;
      thumbnail.setAttribute('aria-hidden', 'true');
      this.appendOptionImage(thumbnail, 'stitchingStyle', option, [document.createElement('i'), document.createElement('i')]);
      return thumbnail;
    }

    createStitchingStyleOption(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__style-card';
      label.dataset.optionId = option.id;
      this.setPriceModifierMetadata(label, 'stitchingStyle', option.id);
      if (option.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-stitchingStyle-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'stitchingStyle';
      input.dataset.csCdFocusTarget = 'stitchingStyle';
      const helperId = `cs-cd-${this.root.dataset.sectionId || 'section'}-stitchingStyle-${option.id}-helper`;
      input.setAttribute('aria-describedby', helperId);

      const content = document.createElement('span');
      content.className = 'cs-cd__style-card-content';
      const copy = document.createElement('span');
      copy.className = 'cs-cd__style-card-copy';
      const title = document.createElement('strong');
      title.textContent = this.optionDisplayLabel(option.title, 'stitchingStyle', option.id);
      const helper = document.createElement('small');
      helper.id = helperId;
      helper.className = 'cs-cd__card-hint';
      helper.textContent = option.helperText;
      copy.append(title, helper);
      content.append(this.createStitchingStyleThumbnail(option), copy);
      label.append(input, content);

      const item = document.createElement('div');
      item.className = 'cs-cd__style-item';
      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${option.title} stitching style preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openStitchingStyleZoom(option, zoomButton);
      });
      item.append(label, zoomButton);
      return item;
    }

    createTipShapeThumbnail(option) {
      const thumbnail = document.createElement('span');
      thumbnail.className = 'cs-cd__style-thumbnail cs-cd__tip-shape-thumbnail';
      thumbnail.dataset.thumbnailKind = option.thumbnail.kind;
      thumbnail.dataset.thumbnailVariant = option.thumbnail.variant;
      thumbnail.setAttribute('aria-hidden', 'true');
      this.appendOptionImage(thumbnail, 'tipShape', option, [document.createElement('i')]);
      return thumbnail;
    }

    createTipShapeOption(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__style-card';
      label.dataset.optionId = option.id;
      label.dataset.priceModifierHook = `tipShape:${option.id}`;
      label.dataset.priceModifier = option.priceModifier == null ? '' : String(option.priceModifier);
      if (option.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-tipShape-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'tipShape';
      input.dataset.csCdFocusTarget = 'tipShape';
      const helperId = `cs-cd-${this.root.dataset.sectionId || 'section'}-tipShape-${option.id}-helper`;
      input.setAttribute('aria-describedby', helperId);

      const content = document.createElement('span');
      content.className = 'cs-cd__style-card-content';
      const copy = document.createElement('span');
      copy.className = 'cs-cd__style-card-copy';
      const title = document.createElement('strong');
      title.textContent = option.title;
      const helper = document.createElement('small');
      helper.id = helperId;
      helper.className = 'cs-cd__card-hint';
      helper.textContent = option.helperText;
      copy.append(title, helper);
      content.append(this.createTipShapeThumbnail(option), copy);
      label.append(input, content);

      const item = document.createElement('div');
      item.className = 'cs-cd__style-item';
      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${option.title} tip shape preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openTipShapeZoom(option, zoomButton);
      });
      item.append(label, zoomButton);
      return item;
    }

    createEdgeFinishingOption(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__edge-finishing-option';
      label.dataset.optionId = option.id;
      label.dataset.priceModifierHook = `edgeFinishing:${option.id}`;
      label.dataset.priceModifier = option.priceModifier == null ? '' : String(option.priceModifier);
      if (option.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-edgeFinishing-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'edgeFinishing';
      input.dataset.csCdFocusTarget = 'edgeFinishing';

      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = option.title;
      title.dataset.csCdEdgeFinishingTitle = option.id;
      const status = document.createElement('small');
      status.dataset.csCdEdgeFinishingStatus = option.id;
      copy.append(title, status);
      label.append(input, copy);
      return label;
    }

    createEdgeColourOption(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__paint-colour-option';
      label.dataset.colourId = option.id;
      label.dataset.priceModifierHook = `edgeColour:${option.id}`;
      label.dataset.priceModifier = option.priceModifier == null ? '' : String(option.priceModifier);
      if (option.disabled) label.dataset.disabled = 'true';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-edgeColour-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'edgeColour';
      input.dataset.csCdFocusTarget = 'edgeColour';
      input.setAttribute('aria-label', option.helperText);

      const visual = document.createElement('span');
      visual.className = 'cs-cd__paint-colour-image';
      visual.setAttribute('aria-hidden', 'true');
      const imageUrl = this.edgeColourAssets.get(option.id);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        visual.append(image);
      }
      const title = document.createElement('span');
      title.textContent = option.title;
      label.append(input, visual, title);
      return label;
    }

    createThreadColourSwatch(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__thread-colour-swatch';
      label.title = option.title;
      label.dataset.optionId = option.id;
      label.dataset.priceModifierHook = `threadColour:${option.id}`;
      label.dataset.priceModifier = option.priceModifier == null ? '' : String(option.priceModifier);

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-threadColour-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'threadColour';
      input.dataset.csCdFocusTarget = 'threadColour';
      input.setAttribute('aria-label', option.title);

      const visual = document.createElement('span');
      visual.className = 'cs-cd__thread-colour-image';
      visual.dataset.thumbnailVariant = option.thumbnail.variant;
      visual.setAttribute('aria-hidden', 'true');
      const asset = this.threadColourAssets.get(option.id);
      if (asset?.imageUrl) {
        const image = document.createElement('img');
        image.src = asset.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        visual.append(image);
      }

      const title = document.createElement('span');
      title.className = 'cs-cd__thread-colour-name';
      title.textContent = option.title;
      label.append(input, visual, title);

      const item = document.createElement('div');
      item.className = 'cs-cd__thread-colour-item';
      const zoomButton = document.createElement('button');
      zoomButton.className = 'cs-cd__style-zoom-button cs-cd__thread-colour-zoom-button';
      zoomButton.type = 'button';
      zoomButton.setAttribute('aria-label', `Enlarge ${option.title} stitch colour preview`);
      zoomButton.setAttribute('aria-haspopup', 'dialog');
      zoomButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openThreadColourZoom(option, zoomButton);
      });
      item.append(label, zoomButton);
      return item;
    }

    renderStitchingOptions() {
      if (!this.stitchingDataApi || this.renderedStitchingOptions) return;
      const stitchingGrid = this.root.querySelector('[data-cs-cd-stitching-options]');
      const threadGrid = this.root.querySelector('[data-cs-cd-thread-colour-options]');
      if (stitchingGrid) stitchingGrid.replaceChildren(...this.stitchingDataApi.getOptions('stitchingStyle').map((option) => this.createStitchingStyleOption(option)));
      if (threadGrid) threadGrid.replaceChildren(...this.stitchingDataApi.getCustomThreadColours().map((option) => this.createThreadColourSwatch(option)));
      this.renderedStitchingOptions = true;
    }

    renderFinishingHardwareOptions() {
      if (!this.finishingHardwareDataApi || this.renderedFinishingHardwareOptions) return;
      for (const field of ['tipShape', 'edgeFinishing', 'edgeColour', 'springBars']) {
        const grid = this.root.querySelector(`[data-cs-cd-finishing-options="${field}"]`);
        if (!grid) continue;
        if (field === 'springBars') {
          const options = this.finishingHardwareDataApi.getOptions(field).map((item) => {
            const option = document.createElement('option');
            option.value = item.id;
            option.textContent = item.title.replace(/\s+Spring Bars$/i, '');
            option.disabled = Boolean(item.disabled);
            option.dataset.priceModifierHook = `${field}:${item.id}`;
            option.dataset.priceModifier = item.priceModifier == null ? '' : String(item.priceModifier);
            return option;
          });
          grid.replaceChildren(...options);
          continue;
        }
        grid.replaceChildren(...this.finishingHardwareDataApi.getOptions(field)
          .filter((option) => field !== 'edgeColour' || option.id !== 'on-tone')
          .map((option) => {
            if (field === 'tipShape') return this.createTipShapeOption(option);
            if (field === 'edgeFinishing') return this.createEdgeFinishingOption(option);
            return this.createEdgeColourOption(option);
          }));
      }
      this.renderedFinishingHardwareOptions = true;
    }

    renderBuckleOptions() {
      if (!this.buckleDataApi || this.renderedBuckleOptions) return;
      const grid = this.root.querySelector('[data-cs-cd-buckle-options]');
      if (grid) {
        const panels = ['classic', 'butterfly'].map((type) => {
          const panel = document.createElement('div');
          panel.id = `cs-cd-buckle-panel-${type}-${this.root.dataset.sectionId || 'section'}`;
          panel.className = 'cs-cd__buckle-panel';
          panel.dataset.csCdBucklePanel = type;
          panel.setAttribute('role', 'tabpanel');
          panel.replaceChildren(...this.buckleDataApi.getOptions().filter((option) => option.type === type).map((option) => this.createBuckleOption(option)));
          return panel;
        });
        grid.replaceChildren(...panels);
      }
      this.root.querySelectorAll('[data-cs-cd-buckle-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.activateBuckleType(tab.dataset.csCdBuckleTab, { selectMatchingFinish: true }));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const nextType = event.key === 'ArrowLeft' || event.key === 'Home' ? 'classic' : 'butterfly';
          this.activateBuckleType(nextType, { focus: true, selectMatchingFinish: true });
        });
      });
      this.root.querySelector('[data-cs-cd-buckle-own]')?.addEventListener('click', () => {
        this.setRawValue('buckle', 'none');
        if (this.buckleValidationAttempted) this.validateBuckle();
      });
      this.renderedBuckleOptions = true;
    }

    createBuckleOption(option) {
      const label = document.createElement('label');
      label.className = 'cs-cd__buckle-option';
      label.dataset.optionId = option.id;
      this.setPriceModifierMetadata(label, 'buckle', option.id);

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cs-cd-buckle-${this.root.dataset.sectionId || 'section'}`;
      input.value = option.id;
      input.required = true;
      input.disabled = Boolean(option.disabled);
      input.dataset.csCdStateField = 'buckle';
      input.dataset.csCdFocusTarget = 'buckle';
      input.setAttribute('aria-label', this.optionDisplayLabel(option.title, 'buckle', option.id));

      const imageWrap = document.createElement('span');
      imageWrap.className = 'cs-cd__buckle-image';
      const imageUrl = this.buckleAssets.get(`${option.type}:${option.finish}`);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        imageWrap.append(image);
      }
      const name = document.createElement('span');
      name.className = 'cs-cd__buckle-name';
      name.textContent = this.optionDisplayLabel(option.finishLabel, 'buckle', option.id);
      const check = document.createElement('span');
      check.className = 'cs-cd__buckle-check';
      check.setAttribute('aria-hidden', 'true');
      label.append(input, imageWrap, name, check);
      return label;
    }

    activateBuckleType(type, options) {
      if (!['classic', 'butterfly'].includes(type)) return;
      const selected = this.buckleDataApi?.getOption(this.rawState.buckle);
      this.activeBuckleType = type;
      if (options?.selectMatchingFinish && selected?.finish && selected.type !== type) {
        const match = this.buckleDataApi.getOptions().find((option) => option.type === type && option.finish === selected.finish && !option.disabled);
        if (match) this.setRawValue('buckle', match.id);
      }
      this.renderBuckleType();
      if (options?.focus) this.root.querySelector(`[data-cs-cd-buckle-tab="${type}"]`)?.focus();
    }

    renderBuckleType() {
      const selector = this.root.querySelector('[data-cs-cd-buckle-selector]');
      if (selector) setData(selector, 'activeBuckleType', this.activeBuckleType);
      this.root.querySelectorAll('[data-cs-cd-buckle-tab]').forEach((tab) => {
        const active = tab.dataset.csCdBuckleTab === this.activeBuckleType;
        tab.classList.toggle('is-active', active);
        setAttr(tab, 'aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.root.querySelectorAll('[data-cs-cd-buckle-panel]').forEach((panel) => {
        const active = panel.dataset.csCdBucklePanel === this.activeBuckleType;
        setFlag(panel, 'hidden', !active);
      });
    }

    renderPaddingThicknessOptions() {
      if (!this.paddingThicknessDataApi) return;
      const paddingGrid = this.root.querySelector('[data-cs-cd-padding-options]');
      if (paddingGrid && !this.renderedPaddingOptions) {
        paddingGrid.replaceChildren(...this.paddingThicknessDataApi.DATA.padding.map((option) => this.createPaddingOption(option)));
        this.renderedPaddingOptions = true;
      }
      for (const route of ['standard', 'folding']) {
        const field = route === 'standard' ? 'thickness' : 'foldingThickness';
        const select = this.root.querySelector(`[data-cs-cd-thickness-options="${route}"]`);
        const renderKey = route === 'standard' ? this.rawState.padding : route;
        if (select && this[`rendered${route}ThicknessOptions`] !== renderKey) {
          this.renderDimensionSelect(select, 'Choose an option', field, this.paddingThicknessDataApi.getThicknessOptions(route, this.rawState.padding));
          this[`rendered${route}ThicknessOptions`] = renderKey;
        }
      }
    }

    renderLengthOptions() {
      const route = this.dimensionsDataApi?.getLengthRouteId(this.rawState.style);
      const select = this.root.querySelector('[data-cs-cd-dimension-options="lengthPreset"]');
      if (!select || !this.dimensionsDataApi || route === this.renderedLengthRoute) return;
      const options = this.dimensionsDataApi.getLengthOptions(this.rawState.style);
      this.renderDimensionSelect(select, 'Select option', 'lengthPreset', options);
      this.renderedLengthRoute = route;
    }

    renderCanonicalOptionGroups() {
      if (!this.styleDataApi) return;
      ['style', 'curvedEnd'].forEach((field) => {
        const grid = this.root.querySelector(`[data-cs-cd-option-grid="${field}"]`);
        if (!grid) return;
        grid.replaceChildren(...this.styleDataApi.getOptions(field).map((option) => this.createOptionCard(field, option)));
      });
      const categories = this.root.querySelector('[data-cs-cd-material-categories]');
      this.replaceOptions(categories, 'Select option', this.preconfigurationApi.DATA.categories, this.rawState.materialCategory);
      this.replaceLiningOptions(this.rawState.liningMaterial);
      if (this.dimensionsDataApi) {
        [['lugWidth', 'lug'], ['buckleWidth', 'buckle']].forEach(([field, kind]) => {
          const select = this.root.querySelector(`[data-cs-cd-dimension-options="${field}"]`);
          this.renderDimensionSelect(select, 'Choose an option', field, this.dimensionsDataApi.getWidthOptions(kind));
        });
        this.renderLengthOptions();
      }
      this.renderPaddingThicknessOptions();
      this.renderStitchingOptions();
      this.renderFinishingHardwareOptions();
      this.renderBuckleOptions();
    }

    onOptionKeydown(event) {
      const input = event.currentTarget;
      const field = input.dataset.csCdStateField;
      const options = Array.from(this.root.querySelectorAll(`[data-cs-cd-state-field="${field}"]`)).filter((item) => !item.disabled);
      if (!options.length) return;
      const currentIndex = options.indexOf(input);
      let nextIndex = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = options.length - 1;
      else if (event.key === 'Enter' || event.key === ' ') nextIndex = currentIndex;

      if (nextIndex === null) return;
      event.preventDefault();
      const next = options[nextIndex];
      next.checked = true;
      next.focus();
      this.setRawValue(field, next.value);
    }

    // Typed fields pass { deferRender: true }: state updates at once, the DOM renders at most once per frame.
    // submitOrder and validation read rawState, not the DOM, so they never see a stale value.
    setRawValue(field, value, { deferRender = false } = {}) {
      if (this.trackingArmed) trackBuilderEvent('customize_start', { first_field: field });
      const previousMaterial = this.rawState.material;
      const previousEdge = { finishing: this.rawState.edgeFinishing, colour: this.rawState.edgeColour };
      if (this.preconfigurationApi && ['materialCategory', 'material', 'materialColour'].includes(field)) {
        this.rawState = this.preconfigurationApi.transition(this.rawState, this.stateApi, field, value);
      } else if (this.liningDataApi && ['liningMaterial', 'liningColour'].includes(field)) {
        this.rawState = this.liningDataApi.transition(this.rawState, this.stateApi, field, value);
      } else {
        this.rawState = this.stateApi.updateRawState(this.rawState, field, value);
      }
      if (['materialCategory', 'material'].includes(field) && this.rawState.edgeColour === 'on-tone') {
        this.edgeColourMode = 'on-tone';
      }
      if (['materialCategory', 'material'].includes(field) && this.rawState.material !== previousMaterial) {
        this.rawState = this.stateApi.updateRawState(this.rawState, 'keeper1Colour', 'match-main');
        this.rawState = this.stateApi.updateRawState(this.rawState, 'keeper2Colour', 'match-main');
        if (this.rawState.material) {
          if (!this.rawState.stitchingStyle) this.rawState = this.stateApi.updateRawState(this.rawState, 'stitchingStyle', 'classic-stitches');
          if (!this.rawState.threadColour) this.rawState = this.stateApi.updateRawState(this.rawState, 'threadColour', 'cream');
        }
      }
      if (this.paddingThicknessDataApi?.normalizeState) {
        const thicknessBefore = this.rawState.thickness;
        this.rawState = this.paddingThicknessDataApi.normalizeState(this.rawState, { paddingChanged: field === 'padding' });
        this.updateThicknessNotice(field, thicknessBefore);
      }
      if (this.finishingHardwareDataApi?.normalizeState) {
        this.rawState = this.finishingHardwareDataApi.normalizeState(this.rawState, {
          deferWhenMaterialMissing: field === 'materialCategory'
        });
      }
      this.updateEdgeNotice(field, previousEdge);
      this.resetOrderFeedback();
      this.effectiveState = this.stateApi.toEffectiveState(this.rawState, this.preconfigurationApi, this.liningDataApi, this.dimensionsDataApi, this.paddingThicknessDataApi, this.stitchingDataApi, this.finishingHardwareDataApi, this.buckleDataApi, this.additionalCustomisationDataApi, this.styleDataApi);
      this.derivedState = this.stateApi.toDerivedState(this.rawState, this.effectiveState, this.preconfigurationApi, this.liningDataApi, this.dimensionsDataApi, this.paddingThicknessDataApi, this.stitchingDataApi, this.finishingHardwareDataApi, this.buckleDataApi, this.additionalCustomisationDataApi, this.styleDataApi);
      if (deferRender) {
        this.renderFrame ||= requestAnimationFrame(() => {
          this.renderFrame = 0;
          this.renderState();
        });
        return;
      }
      if (this.renderFrame) {
        cancelAnimationFrame(this.renderFrame);
        this.renderFrame = 0;
      }
      this.renderState();
    }

    resetOrderFeedback() {
      if (this.orderForm?.dataset.state !== 'loading') {
        this.orderRequestId = null;
        this.setOrderState('idle', '');
      }
    }

    // "What's Included" follows the configuration: no buckle when the customer uses their own, no standard spring bars for Middle Cut.
    renderIncluded() {
      const buckleItem = this.root.querySelector('[data-cs-cd-included="buckle"]');
      if (buckleItem && this.buckleDataApi) {
        const ownBuckle = this.rawState.buckle === 'none' || !this.buckleDataApi.isCatalogueActive(this.rawState);
        const buckle = ownBuckle ? null : this.buckleDataApi.getOption(this.rawState.buckle);
        setText(buckleItem, ownBuckle
          ? 'No buckle: you are using your own'
          : buckle
            ? `1 x ${buckle.title}${this.rawState.noCernirLogo === true ? ', plain (no logo)' : ''}, attached to the strap`
            : 'The buckle you select, attached to the strap');
      }
      const springItem = this.root.querySelector('[data-cs-cd-included="spring-bars"]');
      if (springItem && this.finishingHardwareDataApi) {
        if (!this.finishingHardwareDataApi.isSpringBarsActive(this.rawState.style)) {
          setText(springItem, 'Spring bars: Middle Cut uses a different attachment, so standard spring bars are not assumed. Contact us if you are unsure what your watch needs.');
        } else {
          const bars = this.finishingHardwareDataApi.getOptions('springBars').find((item) => item.id === this.rawState.springBars);
          setText(springItem, bars ? `2 x ${bars.title}, fitted to the strap ends` : 'The spring bars you select, fitted to the strap ends');
        }
      }
    }

    // Tell the customer when changing padding also changed their thickness, right under the thickness field.
    updateThicknessNotice(field, thicknessBefore) {
      const notice = this.root.querySelector('[data-cs-cd-thickness-notice]');
      if (!notice) return;
      if (field === 'padding' && thicknessBefore && this.rawState.thickness && this.rawState.thickness !== thicknessBefore) {
        const thickness = this.paddingThicknessDataApi.getThicknessOption('standard', this.rawState.thickness);
        const padding = this.paddingThicknessDataApi.getPaddingOption(this.rawState.padding);
        if (thickness && padding) {
          setText(notice, `Thickness changed to ${thickness.label} to match ${padding.label.replace(/\s*\(Default\)$/, '')} padding. You can still choose another.`);
          setFlag(notice, 'hidden', false);
          return;
        }
      }
      if (field !== 'padding') {
        setFlag(notice, 'hidden', true);
        setText(notice, '');
      }
    }

    // Folded Edges (a paid option) and a chosen edge paint can be switched back by another choice; say so under
    // Edge Finishing instead of letting the price drop silently. Cleared by the next change to the edge fields.
    updateEdgeNotice(field, previousEdge) {
      const fieldset = this.root.querySelector('[data-cs-cd-finishing-options="edgeFinishing"]')?.closest('fieldset, .cs-cd__field');
      if (!fieldset) return;
      let notice = fieldset.querySelector('[data-cs-cd-edge-notice]');
      const messages = [];
      if (previousEdge.finishing === 'folded-edges' && this.rawState.edgeFinishing !== 'folded-edges' && field !== 'edgeFinishing') {
        const rule = this.finishingHardwareDataApi.getFoldedEdgeRule(this.rawState.material);
        messages.push(rule.supported
          ? 'Edge finishing changed to Painted Edges: Folded Edges are not available with the Pilot tip.'
          : 'Edge finishing changed to Painted Edges: Folded Edges are made in Epsom, Togo, Alran Chèvre or Alligator only.');
      }
      if (previousEdge.colour && previousEdge.colour !== 'on-tone' && this.rawState.edgeColour === 'on-tone' && !['edgeColour', 'edgeFinishing'].includes(field)) {
        messages.push('Edge colour changed back to tone-on-tone for the new leather. You can still choose another colour.');
      }
      if (messages.length) {
        if (!notice) {
          notice = document.createElement('p');
          notice.className = 'cs-cd__auto-notice';
          notice.dataset.csCdEdgeNotice = '';
          notice.setAttribute('role', 'status');
          fieldset.append(notice);
        }
        notice.textContent = messages.join(' ');
        notice.hidden = false;
      } else if (notice && ['edgeFinishing', 'edgeColour', 'tipShape', 'material', 'materialCategory'].includes(field)) {
        notice.hidden = true;
      }
    }

    renderState() {
      this.renderPreconfiguration();
      this.renderStyleOptions();
      // toDerivedState already built the same properties (same effective state and catalogs) as configurationSummary.
      this.cartProperties = Object.fromEntries((this.derivedState.configurationSummary || []).map(({ label, value }) => [label, value]));
      this.renderIncluded();
      const leatherGuide = this.root.querySelector('[data-cs-cd-field-guide-open="leather"]');
      if (leatherGuide) setFlag(leatherGuide, 'hidden', this.rawState.materialCategory === 'non-leather');
      this.renderDimensions();
      this.renderStitching();
      this.renderFinishingHardware();
      this.renderBuckle();
      this.renderAdditionalCustomisation();
      this.renderPrice();
      this.renderCompleteSummary();
      this.renderTabStates();
      this.syncOrderAvailability();
      if (this.coreValidationAttempted) this.renderCoreErrors(this.stateApi.validateCore(this.rawState, this.preconfigurationApi, this.styleDataApi));
      if (this.outerMaterialValidationAttempted) this.renderOuterMaterialErrors(this.stateApi.validateOuterMaterial(this.rawState, this.preconfigurationApi));
      if (this.liningValidationAttempted) this.renderLiningErrors(this.stateApi.validateLining(this.rawState, this.liningDataApi));
      if (this.keeperValidationAttempted) this.renderKeeperErrors(this.stateApi.validateKeepers(this.rawState, this.preconfigurationApi));
      if (this.paddingThicknessValidationAttempted) this.renderPaddingThicknessErrors(this.stateApi.validatePaddingThickness(this.rawState, this.paddingThicknessDataApi));
      if (this.dimensionValidationAttempted) this.renderDimensionErrors(this.stateApi.validateDimensions(this.rawState, this.dimensionsDataApi));
      if (this.stitchingValidationAttempted) this.renderStitchingErrors(this.stateApi.validateStitching(this.rawState, this.stitchingDataApi));
      if (this.finishingHardwareValidationAttempted) this.renderFinishingHardwareErrors(this.stateApi.validateFinishingHardware(this.rawState, this.finishingHardwareDataApi));
      if (this.buckleValidationAttempted) this.renderBuckleErrors(this.stateApi.validateBuckle(this.rawState, this.buckleDataApi));
      if (this.additionalCustomisationValidationAttempted) this.renderAdditionalCustomisationErrors(this.stateApi.validateAdditionalCustomisation(this.rawState, this.additionalCustomisationDataApi));
      const detail = {
        rawState: this.stateApi.createRawState(this.rawState),
        effectiveState: { ...this.effectiveState },
        derivedState: { ...this.derivedState },
        cartProperties: { ...this.cartProperties },
        priceBreakdown: this.priceBreakdown ? { ...this.priceBreakdown } : null
      };

      this.root.dispatchEvent(new CustomEvent('cernir:custom-strap-state-change', { detail }));
      saveDesign(this.rawState);
    }

    renderCompleteSummary() {
      const list = this.root.querySelector('[data-cs-cd-summary-list]');
      const empty = this.root.querySelector('[data-cs-cd-summary-empty]');
      const rows = (this.derivedState.configurationSummary || []).map((row) => ({ ...row, price: this.summaryRowPrice(row.label) }));
      // Rebuilt only when a row changes (most re-renders, e.g. typing notes, leave it as it is).
      const summaryKey = JSON.stringify(rows);
      if (list && summaryKey !== this.renderedSummaryKey) {
        this.renderedSummaryKey = summaryKey;
        const children = [];
        rows.forEach((row) => {
          const term = document.createElement('dt');
          const description = document.createElement('dd');
          const value = document.createElement('span');
          // The order keeps the workshop's property name; the customer sees the word used on the option.
          term.textContent = row.label === 'No CERNIR Logo' ? 'Unbranded' : row.label;
          description.className = 'cs-cd__summary-description';
          value.className = 'cs-cd__summary-value';
          value.textContent = row.value;
          description.append(value);

          if (row.price) {
            const price = document.createElement('span');
            price.className = 'cs-cd__summary-option-price';
            price.textContent = row.price;
            description.append(price);
          }
          children.push(term, description);
        });
        list.replaceChildren(...children);
      }
      if (empty) setFlag(empty, 'hidden', rows.length > 0);
    }

    summaryRowPrice(label) {
      if (!this.priceBreakdown) return '';
      if (label === 'Outer Material') {
        const cents = this.priceBreakdown.basePriceCents;
        return Number.isInteger(cents) && cents >= 0 ? formatMoney(cents, this.currencyCode) : '';
      }

      const fieldByLabel = {
        'Strap Style': 'style',
        'Curved End': 'curvedEnd',
        'Lining Material': 'liningMaterial',
        Padding: 'padding',
        Stitching: 'stitchingStyle',
        'Edge Finishing': 'edgeFinishing',
        Buckle: 'buckle',
        'No CERNIR Logo': 'noCernirLogo'
      };
      const field = fieldByLabel[label];
      if (!field) return '';
      const cents = this.priceBreakdown.options?.find((option) => option.field === field)?.priceCents;
      return Number.isInteger(cents) && cents > 0 ? `+${formatMoney(cents, this.currencyCode)}` : '';
    }

    renderCoreErrors(errors) {
      Object.entries(this.coreErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    hasValidationErrors(errors) {
      return Object.values(errors || {}).some(Boolean);
    }



    syncOrderAvailability() {
      if (!this.orderButton || ['loading', 'success'].includes(this.orderForm?.dataset.state)) return;
      setFlag(this.orderButton, 'disabled', false);
      this.orderButton.setAttribute('aria-disabled', 'false');
    }

    focusValidationField(field) {
      const groupId = this.groupForField(field);
      if (groupId) {
        this.attemptedGroups.add(groupId);
        this.activateTab(groupId, { focus: false });
        this.renderTabStates();
      }
      if (!this.focusField(field) || !this.tabsContainer || !window.matchMedia('(max-width: 749px)').matches) return;
      // On a phone the compact preview bar covers the tab row and its "!" marks. When the tabs and the field fit on
      // screen together below that bar, bring the tabs into view instead; the field stays visible under them.
      const target = this.root.querySelector(`[data-cs-cd-focus-target="${field}"]`);
      const targetBox = target.getBoundingClientRect();
      const span = targetBox.bottom - this.tabsContainer.getBoundingClientRect().top;
      const compactBar = parseFloat(window.getComputedStyle(this.root).getPropertyValue('--cs-cd-preview-compact-height')) || 72;
      if (span < window.innerHeight - compactBar - 140) this.scrollTabsIntoView();
    }

    // Checks every tab so each one with a problem gets its "!", then takes the customer to the first problem.
    validateForOrder() {
      let firstField = null;
      let failed = false;
      for (const group of GROUP_DEFINITIONS) {
        if (this.validateTab(group.id, { focus: false })) continue;
        failed = true;
        if (!firstField) {
          const errors = this.groupErrors(group.id);
          firstField = group.fields.find((field) => errors[field]) || null;
        }
      }
      if (failed) {
        if (firstField) this.focusValidationField(firstField);
        return false;
      }
      GROUP_DEFINITIONS.forEach(({ id }) => {
        this.attemptedGroups.add(id);
        this.confirmedGroups.add(id);
      });
      this.renderTabStates();
      return true;
    }

    buildOrderSnapshot() {
      const rawState = this.stateApi.createRawState(this.rawState);
      const effectiveState = this.stateApi.toEffectiveState(rawState, this.preconfigurationApi, this.liningDataApi, this.dimensionsDataApi, this.paddingThicknessDataApi, this.stitchingDataApi, this.finishingHardwareDataApi, this.buckleDataApi, this.additionalCustomisationDataApi, this.styleDataApi);
      const priceBreakdown = this.commerceApi.calculatePriceBreakdown(effectiveState, this.preconfigurationApi, this.commerceContract);
      const variant = Number.isInteger(priceBreakdown?.totalCents)
        ? this.commerceApi.findExactAvailableVariant(this.variants, priceBreakdown.totalCents)
        : null;
      const cartProperties = this.stateApi.toCartProperties(effectiveState, this.preconfigurationApi, this.liningDataApi, this.dimensionsDataApi, this.paddingThicknessDataApi, this.stitchingDataApi, this.finishingHardwareDataApi, effectiveState, this.buckleDataApi, this.additionalCustomisationDataApi, this.styleDataApi);
      return Object.freeze({ rawState, effectiveState, priceBreakdown, variant, cartProperties: Object.freeze({ ...cartProperties }) });
    }

    buildCartFormData(order) {
      const formData = new FormData();
      formData.append('id', String(order.variant.id));
      formData.append('quantity', '1');
      Object.entries(order.cartProperties).forEach(([key, value]) => {
        if (value && key !== 'Buckle Image Upload' && key !== 'Related Image Uploads') {
          formData.append(`properties[${key}]`, value);
        }
      });
      // The workshop picks the spool by its Meisi code; the "_" prefix keeps it out of the cart, checkout and emails.
      const threadCode = order.cartProperties['Stitch Colour'] ? this.stitchingDataApi?.getOption('threadColour', order.effectiveState.threadColour)?.code : '';
      if (threadCode) formData.append('properties[_Thread Code]', threadCode);
      formData.append('properties[_Configuration Version]', this.commerceApi.CONFIGURATION_VERSION);
      if (order.priceBreakdown) {
        formData.append('properties[_Pricing Version]', order.priceBreakdown.pricingVersion);
        formData.append('properties[_Base Price Cents]', String(order.priceBreakdown.basePriceCents));
        formData.append('properties[_Options Price Cents]', String(order.priceBreakdown.optionsPriceCents));
        formData.append('properties[_Calculated Total Cents]', String(order.priceBreakdown.totalCents));
        formData.append('properties[_Price Tier]', this.commerceApi.priceTierOptionValue(order.priceBreakdown.totalCents));
      }
      Array.from(order.effectiveState.buckleUpload || []).forEach((file, index) => {
        const suffix = index ? ` ${index + 1}` : '';
        formData.append(`properties[Buckle Image Upload${suffix}]`, file, file.name);
      });
      Array.from(order.effectiveState.relatedImages || []).forEach((file, index) => {
        formData.append(`properties[Related Image Upload ${index + 1}]`, file, file.name);
      });
      return formData;
    }

    setOrderState(state, message) {
      if (this.orderForm) setData(this.orderForm, 'state', state);
      this.orderForm?.setAttribute('aria-busy', String(state === 'loading'));
      if (this.orderStatus) setText(this.orderStatus, message || '');
      if (this.orderButton) {
        setFlag(this.orderButton, 'disabled', state === 'loading' || state === 'success');
        this.orderButton.setAttribute('aria-disabled', String(this.orderButton.disabled));
        setText(this.orderButton, state === 'loading' ? 'Adding…' : (state === 'success' ? 'Added to cart' : this.orderButtonLabel));
      }
    }

    // XHR rather than fetch so large photo uploads can show their progress.
    postCartAdd(url, formData) {
      const hasFiles = Array.from(formData.values()).some((value) => value instanceof File && value.size > 0);
      return new Promise((resolve) => {
        const request = new XMLHttpRequest();
        request.open('POST', url);
        request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        request.setRequestHeader('Accept', 'application/json');
        if (hasFiles) {
          request.upload.addEventListener('progress', (event) => {
            if (!event.lengthComputable || !this.orderStatus) return;
            const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
            this.orderStatus.textContent = `Uploading your files… ${percent}%`;
          });
        }
        request.addEventListener('load', () => {
          let payload = {};
          try { payload = JSON.parse(request.responseText || '{}'); } catch (_error) { payload = {}; }
          resolve({ ok: request.status >= 200 && request.status < 300, payload });
        });
        request.addEventListener('error', () => resolve({ networkError: true, payload: {} }));
        request.addEventListener('abort', () => resolve({ networkError: true, payload: {} }));
        request.send(formData);
      });
    }

    async cartHasRequest(routeRoot, requestId) {
      try {
        const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = await response.json();
        return (cart.items || []).some((item) => item.properties?.['_Order Request'] === requestId);
      } catch (_error) {
        return false;
      }
    }

    async submitOrder(event) {
      event?.preventDefault();
      if (this.isSubmitting) return false;
      this.isSubmitting = true;
      try {
        this.setOrderState('idle', '');
        if (!this.materialCatalogAvailable) {
          this.setOrderState('error', 'Custom materials are temporarily unavailable. Please try again later.');
          this.orderStatus?.focus();
          return false;
        }
        if (!this.validateForOrder()) {
          const tabNames = GROUP_DEFINITIONS.filter(({ id }) => this.groupHasErrors(id))
            .map(({ label }) => label.charAt(0) + label.slice(1).toLowerCase());
          const where = tabNames.length ? ` in ${tabNames.length > 1 ? `${tabNames.slice(0, -1).join(', ')} and ${tabNames[tabNames.length - 1]}` : tabNames[0]}` : '';
          this.setOrderState('error', `Please complete the highlighted options${where} before adding this strap to your cart.`);
          return false;
        }
        const order = this.buildOrderSnapshot();
        this.priceBreakdown = order.priceBreakdown;
        const totalCents = order.priceBreakdown?.totalCents;
        if (!Number.isInteger(totalCents)) {
          this.setOrderState('error', 'This configuration does not have an available commerce price. Please choose another material, lining or padding option.');
          this.orderStatus?.focus();
          return false;
        }
        if (!order.variant) {
          this.setOrderState('error', 'The exact Shopify variant for this configuration is missing or unavailable. Please try another configuration.');
          this.orderStatus?.focus();
          return false;
        }

        this.setOrderState('loading', 'Adding your configuration to cart…');
        try {
          const routeRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          // One id per configuration being added: if the connection drops after Shopify saved the line, the cart is
          // checked for it instead of asking the customer to add (and pay for) it twice.
          this.orderRequestId ||= `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
          const formData = this.buildCartFormData(order);
          formData.append('properties[_Order Request]', this.orderRequestId);
          const result = await this.postCartAdd(`${routeRoot}cart/add.js`, formData);
          if (result.networkError && !(await this.cartHasRequest(routeRoot, this.orderRequestId))) {
            throw new Error('The connection was interrupted. Please check your connection and try again.');
          }
          if (!result.networkError && !result.ok) throw new Error(result.payload.description || 'Unable to add this configuration to cart.');
          this.orderRequestId = null;
          this.setOrderState('success', 'Configuration added. Opening your cart…');
          window.location.assign(`${routeRoot}cart`);
          return true;
        } catch (error) {
          this.setOrderState('error', error?.message || 'Unable to add this configuration to cart. Please try again.');
          this.orderStatus?.focus();
          return false;
        }
      } finally {
        this.isSubmitting = false;
      }
    }

    renderStyleOptions() {
      const curvedEndUnavailable = ['wings-cut', 'middles-cut'].includes(this.rawState.style);
      ['style', 'curvedEnd'].forEach((field) => {
        this.root.querySelectorAll(`[data-cs-cd-state-field="${field}"]`).forEach((input) => {
          if (field === 'curvedEnd') {
            const disabled = curvedEndUnavailable && input.value === 'yes';
            setFlag(input, 'disabled', disabled);
            if (input.parentElement) setData(input.parentElement, 'disabled', String(disabled));
          }
          input.checked = input.value === this.rawState[field];
          const card = input.parentElement;
          if (card) setData(card, 'selected', String(input.checked));
        });
      });

      const selectedStyle = this.styleDataApi?.getOption('style', this.rawState.style);
      if (this.styleCurrent) setText(this.styleCurrent, selectedStyle?.title || '');
      const selectedCurvedEnd = this.styleDataApi?.getOption('curvedEnd', this.rawState.curvedEnd);
      if (this.curvedEndCurrent) setText(this.curvedEndCurrent, selectedCurvedEnd?.title || '');
      const curvedEndCompatibilityMessage = this.root.querySelector('[data-cs-cd-curved-end-compatibility]');
      if (curvedEndCompatibilityMessage) setFlag(curvedEndCompatibilityMessage, 'hidden', !curvedEndUnavailable);
      const curvedEndNote = this.root.querySelector('[data-cs-cd-curved-end-note]');
      if (curvedEndNote) setFlag(curvedEndNote, 'hidden', curvedEndUnavailable || this.rawState.curvedEnd !== 'yes');

      this.root.querySelectorAll('[data-cs-cd-style-measurement-hook]').forEach((hook) => {
        setFlag(hook, 'hidden', hook.dataset.csCdStyleMeasurementHook !== (selectedStyle?.measurementHook || ''));
      });
    }

    renderPrice() {
      this.priceBreakdown = this.commerceApi.calculatePriceBreakdown(this.effectiveState, this.preconfigurationApi, this.commerceContract);
      const hasMaterialPrice = Number.isInteger(this.priceBreakdown?.totalCents);
      const optionsPriceCents = hasMaterialPrice
        ? this.priceBreakdown.optionsPriceCents
        : this.commerceApi.calculateOptionsPriceCents(this.effectiveState);
      const cents = (hasMaterialPrice ? this.priceBreakdown.basePriceCents : this.minimumPriceCents) + optionsPriceCents;

      if (this.priceContainer) setFlag(this.priceContainer, 'hidden', false);
      const formattedTotal = formatMoney(cents, this.currencyCode);
      if (this.priceValue) setText(this.priceValue, formattedTotal);
      (this.summaryTotals || []).forEach((total) => { setText(total, formattedTotal); });
    }


    renderOuterMaterialErrors(errors) {
      Object.entries(this.outerMaterialErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateOuterMaterial(options) {
      this.outerMaterialValidationAttempted = true;
      const errors = this.stateApi.validateOuterMaterial(this.rawState, this.preconfigurationApi);
      this.renderOuterMaterialErrors(errors);
      const firstField = ['materialCategory', 'material', 'materialColour'].find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }


    renderLiningErrors(errors) {
      Object.entries(this.liningErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateLining(options) {
      this.liningValidationAttempted = true;
      const errors = this.stateApi.validateLining(this.rawState, this.liningDataApi);
      this.renderLiningErrors(errors);
      const firstField = ['liningMaterial', 'liningColour'].find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }


    renderKeeperErrors(errors) {
      Object.entries(this.keeperErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateKeepers(options) {
      this.keeperValidationAttempted = true;
      const errors = this.stateApi.validateKeepers(this.rawState, this.preconfigurationApi);
      this.renderKeeperErrors(errors);
      const firstField = ['keeperColourMode', 'keeper1Colour', 'keeper2Colour'].find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderDimensions() {
      if (!this.dimensionsDataApi) return;
      this.renderPaddingThicknessOptions();
      this.renderLengthOptions();
      ['lugWidth', 'buckleWidth', 'lengthPreset'].forEach((field) => {
        this.root.querySelectorAll(`[data-cs-cd-state-field="${field}"]`).forEach((input) => {
          if (input.type === 'radio') {
            input.checked = input.value === this.rawState[field];
            if (input.parentElement) setData(input.parentElement, 'selected', String(input.checked));
          } else {
            input.value = this.rawState[field] || '';
          }
        });
      });

      const customLug = this.root.querySelector('[data-cs-cd-dimension-custom="lugWidth"]');
      const customBuckle = this.root.querySelector('[data-cs-cd-dimension-custom="buckleWidth"]');
      const customLength = this.root.querySelector('[data-cs-cd-dimension-custom="lengthPreset"]');
      if (customLug) setFlag(customLug, 'hidden', this.rawState.lugWidth !== 'other');
      if (customBuckle) setFlag(customBuckle, 'hidden', this.rawState.buckleWidth !== 'other');
      if (customLength) setFlag(customLength, 'hidden', this.rawState.lengthPreset !== 'other');

      // Buckle widths wider than the lug width can't be made: disable them and say why, instead of failing at Add to cart.
      const lugWidthMm = this.dimensionsDataApi.resolveWidthMm(this.rawState, 'lug');
      this.root.querySelectorAll('[data-cs-cd-dimension-options="buckleWidth"] option').forEach((option) => {
        if (!option.dataset.baseLabel) setData(option, 'baseLabel', option.textContent);
        const optionMm = Number.parseFloat(option.value);
        const tooWide = Boolean(lugWidthMm) && Number.isFinite(optionMm) && optionMm > lugWidthMm;
        setFlag(option, 'disabled', tooWide);
        setText(option, tooWide ? `${option.dataset.baseLabel} · wider than your lugs` : option.dataset.baseLabel);
      });

      // Show the chosen taper as one line, e.g. "Your strap: 20 → 16 mm (lug → buckle)".
      const taper = this.root.querySelector('[data-cs-cd-taper]');
      if (taper) {
        const buckleWidthMm = this.dimensionsDataApi.resolveWidthMm(this.rawState, 'buckle');
        const valid = Boolean(lugWidthMm && buckleWidthMm && buckleWidthMm <= lugWidthMm);
        const tooWide = Boolean(lugWidthMm && buckleWidthMm && buckleWidthMm > lugWidthMm);
        setText(taper, valid
          ? `Your strap: ${lugWidthMm} → ${buckleWidthMm} mm (lug → buckle)`
          : tooWide ? `Your buckle width (${buckleWidthMm} mm) is now wider than the lug width (${lugWidthMm} mm). Please choose a narrower buckle width.` : '');
        taper.classList.toggle('cs-cd__field-hint', tooWide);
        setFlag(taper, 'hidden', !(valid || tooWide));
      }

      // Any lug width can be made; only flag values that look like a typo (cm instead of mm, a missing decimal point).
      const lugHint = this.root.querySelector('[data-cs-cd-lug-width-hint]');
      if (lugHint) {
        const unusual = this.rawState.lugWidth === 'other' && Boolean(lugWidthMm) && (lugWidthMm < 10 || lugWidthMm > 30);
        setFlag(lugHint, 'hidden', !unusual);
      }

      [['customLugWidthMm', this.rawState.customLugWidthMm], ['customBuckleWidthMm', this.rawState.customBuckleWidthMm]].forEach(([field, value]) => {
        const input = this.root.querySelector(`[data-cs-cd-state-field="${field}"]`);
        if (input && input.value !== String(value ?? '')) input.value = value ?? '';
      });
      Object.entries(this.customLengthInputs).forEach(([component, input]) => {
        const value = this.rawState.customLength?.[component] ?? '';
        if (input && input.value !== String(value)) input.value = value;
      });
      const lengthHint = this.root.querySelector('[data-cs-cd-length-hint]');
      if (lengthHint) {
        const longMm = Number.parseFloat(this.rawState.customLength?.longEndMm);
        const shortMm = Number.parseFloat(this.rawState.customLength?.shortEndMm);
        const custom = this.rawState.lengthPreset === 'other';
        const swapped = custom && longMm > 0 && shortMm > 0 && longMm < shortMm;
        const unusual = custom && ((longMm > 0 && (longMm < 80 || longMm > 160)) || (shortMm > 0 && (shortMm < 40 || shortMm > 100)));
        if (!lengthHint.dataset.unusualText) setData(lengthHint, 'unusualText', lengthHint.textContent);
        setText(lengthHint, swapped
          ? 'The long end (with holes) is usually longer than the short end (buckle side). Please check the two fields.'
          : lengthHint.dataset.unusualText);
        setFlag(lengthHint, 'hidden', !(swapped || unusual));
      }

      const wristBased = this.dimensionsDataApi.getLengthRouteId(this.rawState.style) === 'wristBased';
      const lengthFieldset = this.root.querySelector('[data-cs-cd-length-fieldset]');
      const wristMeasurements = this.root.querySelector('[data-cs-cd-wrist-measurements]');
      if (lengthFieldset) setFlag(lengthFieldset, 'hidden', wristBased);
      if (wristMeasurements) setFlag(wristMeasurements, 'hidden', !wristBased);
      this.root.querySelectorAll('[data-cs-cd-state-field="lengthPreset"]').forEach((input) => {
        setFlag(input, 'required', !wristBased);
        setFlag(input, 'disabled', wristBased);
      });
      ['wristLengthMm', 'watchLugToLugMm'].forEach((field) => {
        const input = this.root.querySelector(`[data-cs-cd-state-field="${field}"]`);
        if (!input) return;
        const value = this.rawState[field] ?? '';
        if (input.value !== String(value)) input.value = value;
        setFlag(input, 'required', wristBased);
        setFlag(input, 'disabled', !wristBased);
      });

      this.root.querySelectorAll('[data-cs-cd-style-dimension]').forEach((fieldset) => {
        const definition = this.dimensionsDataApi.getStyleMeasurement(fieldset.dataset.csCdStyleDimension);
        const active = definition?.style === this.rawState.style;
        setFlag(fieldset, 'hidden', !active);
        this.namedMeasurementInputs.filter((input) => input.dataset.csCdNamedMeasurementField === fieldset.dataset.csCdStyleDimension).forEach((input) => {
          const component = input.dataset.csCdNamedMeasurementComponent;
          const value = this.rawState[fieldset.dataset.csCdStyleDimension]?.[component] ?? '';
          if (input.value !== String(value)) input.value = value;
          setFlag(input, 'required', active);
          setFlag(input, 'disabled', !active);
        });
      });
      const lengthHelper = this.root.querySelector('[data-cs-cd-length-helper]');
      if (lengthHelper && !wristBased) {
        setText(lengthHelper, 'Choose the long end / short end size in millimeters.');
      }

      if (this.paddingThicknessDataApi) {
        const thicknessRoute = this.paddingThicknessDataApi.getRoute(this.rawState.style);
        const paddingOption = this.paddingThicknessDataApi.getPaddingOption(this.rawState.padding);
        if (this.paddingCurrent) setText(this.paddingCurrent, paddingOption?.label || 'Choose an option');
        for (const field of ['padding', 'thickness', 'foldingThickness']) {
          this.root.querySelectorAll(`[data-cs-cd-state-field="${field}"]`).forEach((input) => {
            const route = field === 'thickness' ? 'standard' : (field === 'foldingThickness' ? 'folding' : null);
            const active = !route || route === thicknessRoute;
            if (input.type === 'radio') input.checked = input.value === this.rawState[field];
            else input.value = this.rawState[field] || '';
            setFlag(input, 'required', active);
            setFlag(input, 'disabled', !active);
            if (input.type === 'radio' && input.parentElement) setData(input.parentElement, 'selected', String(input.checked && active));
          });
        }
        this.root.querySelectorAll('[data-cs-cd-thickness-fieldset]').forEach((fieldset) => {
          setFlag(fieldset, 'hidden', fieldset.dataset.csCdThicknessFieldset !== thicknessRoute);
        });
        const noThicknessNote = this.root.querySelector('[data-cs-cd-no-thickness-note]');
        if (noThicknessNote) setFlag(noThicknessNote, 'hidden', thicknessRoute !== 'none');
        const customActive = thicknessRoute === 'standard' && this.rawState.thickness === 'custom';
        const customThickness = this.root.querySelector('[data-cs-cd-custom-thickness]');
        if (customThickness) setFlag(customThickness, 'hidden', !customActive);
        Object.entries(this.customThicknessInputs).forEach(([component, input]) => {
          if (!input) return;
          const value = this.rawState.customThickness?.[component] ?? '';
          if (input.value !== String(value)) input.value = value;
          setFlag(input, 'required', customActive);
          setFlag(input, 'disabled', !customActive);
        });
      }
    }

    renderStitching() {
      if (!this.stitchingDataApi) return;
      const colourActive = this.stitchingDataApi.isThreadColourActive(this.rawState.stitchingStyle);
      const quickColourIds = new Set(this.stitchingDataApi.getThreadColourQuickOptions().map((option) => option.id));
      if (quickColourIds.has(this.rawState.threadColour)) this.threadColourMode = this.rawState.threadColour;
      else if (this.rawState.threadColour) this.threadColourMode = 'choose-another-colour';
      const customColourActive = colourActive && this.threadColourMode === 'choose-another-colour';

      this.root.querySelectorAll('[data-cs-cd-state-field="stitchingStyle"]').forEach((input) => {
        input.checked = input.value === this.rawState.stitchingStyle;
        setFlag(input, 'required', true);
        setFlag(input, 'disabled', Boolean(this.stitchingDataApi.getOption('stitchingStyle', input.value)?.disabled));
        if (input.parentElement) setData(input.parentElement, 'selected', String(input.checked));
      });
      const selectedStitchingStyle = this.stitchingDataApi.getOption('stitchingStyle', this.rawState.stitchingStyle);
      if (this.stitchingStyleCurrent) setText(this.stitchingStyleCurrent, selectedStitchingStyle?.title || '');
      this.root.querySelectorAll('[data-cs-cd-state-field="threadColour"]').forEach((input) => {
        input.checked = input.value === this.rawState.threadColour && customColourActive;
        setFlag(input, 'required', customColourActive);
        setFlag(input, 'disabled', !customColourActive || Boolean(this.stitchingDataApi.getOption('threadColour', input.value)?.disabled));
        if (input.parentElement) setData(input.parentElement, 'selected', String(input.checked));
      });

      if (this.threadColourModeSelect) {
        this.threadColourModeSelect.value = this.threadColourMode;
        setFlag(this.threadColourModeSelect, 'required', colourActive);
        setFlag(this.threadColourModeSelect, 'disabled', !colourActive);
      }
      if (this.threadColourPicker) {
        setFlag(this.threadColourPicker, 'hidden', !customColourActive);
        setAttr(this.threadColourPicker, 'aria-hidden', String(!customColourActive));
      }

      const threadFieldset = this.root.querySelector('[data-cs-cd-thread-colour-fieldset]');
      if (threadFieldset) {
        setFlag(threadFieldset, 'hidden', !colourActive);
        setAttr(threadFieldset, 'aria-hidden', String(!colourActive));
      }
      if (!colourActive) this.closeThreadColourZoom();

    }

    renderStitchingErrors(errors) {
      Object.entries(this.stitchingErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateStitching(options) {
      this.stitchingValidationAttempted = true;
      const errors = this.stateApi.validateStitching(this.rawState, this.stitchingDataApi);
      this.renderStitchingErrors(errors);
      const firstField = ['stitchingStyle', 'threadColour'].find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderFinishingHardware() {
      if (!this.finishingHardwareDataApi) return;
      const tipActive = this.finishingHardwareDataApi.isTipShapeActive(this.rawState.style);
      const resolvedTipShape = this.finishingHardwareDataApi.resolveTipShape(this.rawState);
      const selectedTipShape = this.finishingHardwareDataApi.getOptions('tipShape').find((item) => item.id === resolvedTipShape);
      if (this.tipShapeCurrent) setText(this.tipShapeCurrent, selectedTipShape?.title || '');
      const colourActive = this.finishingHardwareDataApi.isEdgeColourActive(this.rawState.edgeFinishing)
        && this.finishingHardwareDataApi.isEdgeFinishingAllowed(this.rawState.edgeFinishing, resolvedTipShape, this.rawState.material);
      if (this.rawState.edgeColour === 'on-tone') this.edgeColourMode = 'on-tone';
      else if (this.rawState.edgeColour) this.edgeColourMode = 'choose-another-colour';
      const customEdgeColourActive = colourActive && this.edgeColourMode === 'choose-another-colour';
      const springBarsActive = this.finishingHardwareDataApi.isSpringBarsActive(this.rawState.style);
      const activeByField = { tipShape: tipActive, edgeFinishing: true, edgeColour: customEdgeColourActive };

      for (const field of Object.keys(activeByField)) {
        this.root.querySelectorAll(`[data-cs-cd-state-field="${field}"]`).forEach((input) => {
          const option = this.finishingHardwareDataApi.getOptions(field).find((item) => item.id === input.value);
          const incompatible = field === 'edgeFinishing'
            && !this.finishingHardwareDataApi.isEdgeFinishingAllowed(input.value, resolvedTipShape, this.rawState.material);
          const active = activeByField[field];
          input.checked = active && input.value === this.rawState[field];
          setFlag(input, 'required', active);
          setFlag(input, 'disabled', !active || incompatible || Boolean(option?.disabled));
          if (input.parentElement) {
            setData(input.parentElement, 'selected', String(input.checked));
            setData(input.parentElement, 'disabled', String(input.disabled));
            if (field === 'edgeFinishing') {
              const cents = this.finishingHardwareDataApi.getEdgeFinishingSurchargeCents(input.value, this.rawState.material);
              const status = input.parentElement.querySelector(`[data-cs-cd-edge-finishing-status="${input.value}"]`);
              if (status) setText(status, input.value === 'painted-edges'
                ? 'Included'
                : incompatible
                  ? (this.finishingHardwareDataApi.getFoldedEdgeRule(this.rawState.material).supported
                    ? 'Not available with Pilot tip'
                    : 'Epsom, Togo, Alran Chèvre or Alligator only')
                  : `+${formatMoney(cents, this.currencyCode)}`);
              setData(input.parentElement, 'priceModifierCents', String(cents));
              setData(input.parentElement, 'priceModifier', String(cents / 100));
            }
          }
        });
      }

      const selectedEdgeFinishing = this.finishingHardwareDataApi.getOption('edgeFinishing', this.rawState.edgeFinishing);
      if (this.edgeFinishingCurrent) setText(this.edgeFinishingCurrent, selectedEdgeFinishing?.title || '');
      if (this.edgeColourModeSelect) {
        this.edgeColourModeSelect.value = this.edgeColourMode;
        setFlag(this.edgeColourModeSelect, 'required', colourActive);
        setFlag(this.edgeColourModeSelect, 'disabled', !colourActive);
      }
      if (this.edgeColourPicker) {
        setFlag(this.edgeColourPicker, 'hidden', !customEdgeColourActive);
        setAttr(this.edgeColourPicker, 'aria-hidden', String(!customEdgeColourActive));
      }
      const springBarsSelect = this.root.querySelector('[data-cs-cd-state-field="springBars"]');
      if (springBarsSelect) {
        springBarsSelect.value = this.rawState.springBars || '';
        setFlag(springBarsSelect, 'required', springBarsActive);
        setFlag(springBarsSelect, 'disabled', !springBarsActive);
      }

      for (const [selector, active] of [
        ['[data-cs-cd-tip-shape-fieldset]', tipActive],
        ['[data-cs-cd-edge-colour-fieldset]', colourActive],
        ['[data-cs-cd-spring-bars-fieldset]', springBarsActive],
        ['[data-cs-cd-spring-bars-note]', !springBarsActive]
      ]) {
        const fieldset = this.root.querySelector(selector);
        if (fieldset) {
          setFlag(fieldset, 'hidden', !active);
          setAttr(fieldset, 'aria-hidden', String(!active));
        }
      }

    }

    renderFinishingHardwareErrors(errors) {
      Object.entries(this.finishingHardwareErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateFinishingHardware(options) {
      this.finishingHardwareValidationAttempted = true;
      const errors = this.stateApi.validateFinishingHardware(this.rawState, this.finishingHardwareDataApi);
      this.renderFinishingHardwareErrors(errors);
      const firstField = this.finishingHardwareErrorOrder.find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderBuckle() {
      if (!this.buckleDataApi) return;
      const catalogueActive = this.buckleDataApi.isCatalogueActive(this.rawState);
      const uploadActive = this.buckleDataApi.isUploadActive(this.rawState);
      const selectedBuckle = this.buckleDataApi.getOption(this.rawState.buckle);
      if (selectedBuckle?.type === 'classic' || selectedBuckle?.type === 'butterfly') this.activeBuckleType = selectedBuckle.type;
      this.renderBuckleType();

      this.root.querySelectorAll('[data-cs-cd-state-field="buckle"]').forEach((input) => {
        const option = this.buckleDataApi.getOption(input.value);
        input.checked = catalogueActive && input.value === this.rawState.buckle;
        setFlag(input, 'required', catalogueActive);
        setFlag(input, 'disabled', !catalogueActive || Boolean(option?.disabled));
        if (input.parentElement) {
          setData(input.parentElement, 'selected', String(input.checked));
          setData(input.parentElement, 'disabled', String(input.disabled));
        }
      });
      const ownButton = this.root.querySelector('[data-cs-cd-buckle-own]');
      if (ownButton) {
        const ownSelected = catalogueActive && this.rawState.buckle === 'none';
        ownButton.classList.toggle('is-selected', ownSelected);
        setAttr(ownButton, 'aria-pressed', String(ownSelected));
      }

      const buckleFieldset = this.root.querySelector('[data-cs-cd-buckle-fieldset]');
      if (buckleFieldset) {
        setFlag(buckleFieldset, 'hidden', !catalogueActive);
        setAttr(buckleFieldset, 'aria-hidden', String(!catalogueActive));
      }
      const uploadFieldset = this.root.querySelector('[data-cs-cd-buckle-upload-fieldset]');
      if (uploadFieldset) {
        setFlag(uploadFieldset, 'hidden', !uploadActive);
        setAttr(uploadFieldset, 'aria-hidden', String(!uploadActive));
      }
      const uploadInput = this.root.querySelector('[data-cs-cd-state-field="buckleUpload"]');
      if (uploadInput) {
        setFlag(uploadInput, 'required', uploadActive);
        setFlag(uploadInput, 'disabled', !uploadActive);
      }
      const uploadStatus = this.root.querySelector('[data-cs-cd-buckle-upload-status]');
      if (uploadStatus) {
        const files = Array.from(this.rawState.buckleUpload || []);
        setText(uploadStatus, files.length ? files.map((file) => file.name).filter(Boolean).join(', ') : 'No files selected');
      }

    }

    renderBuckleErrors(errors) {
      Object.entries(this.buckleErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateBuckle(options) {
      this.buckleValidationAttempted = true;
      const errors = this.stateApi.validateBuckle(this.rawState, this.buckleDataApi);
      this.renderBuckleErrors(errors);
      const firstField = this.buckleErrorOrder.find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderAdditionalCustomisation() {
      if (!this.additionalCustomisationDataApi) return;
      const files = Array.from(this.rawState.relatedImages || []);
      const uploadStatus = this.root.querySelector('[data-cs-cd-related-images-status]');
      if (uploadStatus) setText(uploadStatus, files.length ? `${files.length} file${files.length === 1 ? '' : 's'} selected` : 'No files selected');
      if (this.relatedImagesSelection) setFlag(this.relatedImagesSelection, 'hidden', files.length === 0);
      const filesChanged = files.length !== (this.renderedRelatedImages || []).length || files.some((file, index) => file !== this.renderedRelatedImages[index]);
      if (this.relatedImagesList && filesChanged) {
        this.renderedRelatedImages = files;
        const rows = files.map((file, index) => {
          const row = document.createElement('li');
          const name = document.createElement('span');
          setText(name, file.name || `File ${index + 1}`);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'cs-cd__upload-remove';
          setText(remove, 'Remove');
          setAttr(remove, 'aria-label', `Remove ${name.textContent}`);
          remove.addEventListener('click', () => {
            this.clearRelatedImagesInput();
            this.setRawValue('relatedImages', Array.from(this.rawState.relatedImages || []).filter((_, itemIndex) => itemIndex !== index));
            this.validateAdditionalCustomisation();
          });
          row.append(name, remove);
          return row;
        });
        this.relatedImagesList.replaceChildren(...rows);
      }

      const personalisationActive = this.additionalCustomisationDataApi.isPersonalisationActive(this.rawState.style);
      const personalisationField = this.root.querySelector('[data-cs-cd-personalisation-field]');
      if (personalisationField) {
        setFlag(personalisationField, 'hidden', !personalisationActive);
        setAttr(personalisationField, 'aria-hidden', String(!personalisationActive));
      }
      if (this.personalisationInput) {
        const value = typeof this.rawState.personalisation === 'string' ? this.rawState.personalisation : '';
        if (this.personalisationInput.value !== value && document.activeElement !== this.personalisationInput) this.personalisationInput.value = value;
        setFlag(this.personalisationInput, 'disabled', !personalisationActive);
      }
      const counter = this.root.querySelector('[data-cs-cd-personalisation-counter]');
      if (counter) setText(counter, `${typeof this.rawState.personalisation === 'string' ? this.rawState.personalisation.length : 0}/${this.additionalCustomisationDataApi.DATA.personalisation.maxCharacters}`);

      const noLogoInput = this.root.querySelector('[data-cs-cd-state-field="noCernirLogo"]');
      if (noLogoInput) {
        noLogoInput.checked = this.rawState.noCernirLogo === true;
        const noLogoControl = noLogoInput.parentElement;
        if (noLogoControl) this.setPriceModifierMetadata(noLogoControl, 'noCernirLogo', true);
        const noLogoPrice = this.root.querySelector('[data-cs-cd-no-logo-price]');
        const cents = this.optionSurchargeCents('noCernirLogo', true);
        if (noLogoPrice) setText(noLogoPrice, `(+${formatMoney(cents, this.currencyCode)})`);
      }

    }

    renderAdditionalCustomisationErrors(errors) {
      Object.entries(this.additionalCustomisationErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateAdditionalCustomisation(options) {
      this.additionalCustomisationValidationAttempted = true;
      const errors = this.stateApi.validateAdditionalCustomisation(this.rawState, this.additionalCustomisationDataApi);
      this.renderAdditionalCustomisationErrors(errors);
      const firstField = this.additionalCustomisationErrorOrder.find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderDimensionErrors(errors) {
      Object.entries(this.dimensionErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validateDimensions(options) {
      this.dimensionValidationAttempted = true;
      const errors = this.stateApi.validateDimensions(this.rawState, this.dimensionsDataApi);
      this.renderDimensionErrors(errors);
      const firstField = this.dimensionErrorOrder.find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    renderPaddingThicknessErrors(errors) {
      Object.entries(this.paddingThicknessErrors).forEach(([field, error]) => {
        const message = errors[field] || '';
        if (error) {
          setText(error, message);
          setFlag(error, 'hidden', !message);
        }
        this.root.querySelectorAll(`[data-cs-cd-focus-target="${field}"]`).forEach((input) => input.setAttribute('aria-invalid', String(Boolean(message))));
      });
    }

    validatePaddingThickness(options) {
      this.paddingThicknessValidationAttempted = true;
      const errors = this.stateApi.validatePaddingThickness(this.rawState, this.paddingThicknessDataApi);
      this.renderPaddingThicknessErrors(errors);
      const firstField = this.paddingThicknessErrorOrder.find((field) => errors[field]);
      if (firstField && options?.focus) this.focusField(firstField);
      return !firstField;
    }

    replaceOptions(select, placeholder, items, selectedValue) {
      if (!select) return;
      select.replaceChildren();
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder;
      select.append(placeholderOption);

      items.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.id;
        option.textContent = select === this.materialSelect ? this.materialDisplayLabel(item) : item.label;
        option.selected = item.id === selectedValue;
        select.append(option);
      });
      select.value = selectedValue || '';
    }

    replaceLiningOptions(selectedValue) {
      if (!this.liningMaterialSelect || !this.liningDataApi) return;
      this.liningMaterialSelect.replaceChildren();
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select option';
      this.liningMaterialSelect.append(placeholder);

      this.liningDataApi.DATA.materials.forEach((material) => {
        const option = document.createElement('option');
        option.value = material.id;
        option.textContent = this.optionDisplayLabel(material.label, 'liningMaterial', material.id);
        option.disabled = Boolean(material.disabled);
        option.selected = material.id === selectedValue;
        this.setPriceModifierMetadata(option, 'liningMaterial', material.id);
        this.liningMaterialSelect.append(option);
      });
      this.liningMaterialSelect.value = selectedValue || '';
    }

    renderPreconfiguration() {
      if (!this.preconfigurationApi) return;

      this.root.querySelectorAll('[data-cs-cd-state-field]').forEach((input) => {
        const field = input.dataset.csCdStateField;
        if (input.type === 'radio') input.checked = input.value === this.rawState[field];
      });

      const category = this.preconfigurationApi.DATA.categories.find((item) => item.id === this.rawState.materialCategory) || null;
      const materials = category ? this.preconfigurationApi.getMaterials(category.id) : [];
      const materialPlaceholder = category ? `Choose ${category.label.toLowerCase()}` : 'Choose leather';

      if (this.materialCategorySelect) this.materialCategorySelect.value = category?.id || '';
      if (this.materialSelectors) setData(this.materialSelectors, 'hasCategory', String(Boolean(category)));
      if (this.materialCategoryCurrent) {
        setText(this.materialCategoryCurrent, category ? ` : ${category.label}` : '');
        setFlag(this.materialCategoryCurrent, 'hidden', !category);
      }
      if (this.materialField) setFlag(this.materialField, 'hidden', !category);
      if (this.materialLabel) setText(this.materialLabel, category?.label || 'Leather');

      // Option lists and colour grids are rebuilt only when their contents change (a different category, material or
      // main colour). Otherwise the radio loop above has already synced `checked`, which also keeps keyboard focus.
      if (this.renderedMaterialOptionsKey !== (category?.id || '')) {
        this.replaceOptions(this.materialSelect, materialPlaceholder, materials, this.rawState.material);
        this.renderedMaterialOptionsKey = category?.id || '';
      } else if (this.materialSelect) {
        this.materialSelect.value = this.rawState.material || '';
      }
      if (this.materialSelect) setFlag(this.materialSelect, 'disabled', !category);
      if (this.materialHelper) {
        setText(this.materialHelper, category
          ? `${materials.length} materials available in ${category.label}.`
          : 'Choose a kind of material first.');
      }

      const material = this.preconfigurationApi.getMaterial(this.rawState.material);
      const colours = this.preconfigurationApi.getColours(this.rawState.material);
      if (this.materialColourField) setFlag(this.materialColourField, 'hidden', !material);
      if (this.materialColourLabel) setText(this.materialColourLabel, material ? `${material.label} Colour` : 'Material Colour');
      if (this.materialColourGrid) {
        if (this.renderedMaterialColourKey !== (material?.id || '')) {
          this.materialColourGrid.replaceChildren(...(material ? colours.map((colour) => this.createMaterialColourCard(material, colour)) : []));
          this.renderedMaterialColourKey = material?.id || '';
        }
        setData(this.materialColourGrid, 'disabled', String(!material));
      }
      if (this.colourHelper) {
        setText(this.colourHelper, material
          ? `${colours.length} colours available for ${material.label}.`
          : 'Colour choices are specific to each material.');
      }

      if (this.liningDataApi) {
        const liningMaterial = this.liningDataApi.getMaterial(this.rawState.liningMaterial);
        const liningColours = this.liningDataApi.getColours(this.rawState.liningMaterial);
        if (this.liningMaterialSelect) this.liningMaterialSelect.value = liningMaterial?.id || '';
        if (this.liningColourLabel) setText(this.liningColourLabel, liningMaterial ? `${liningMaterial.label} Colour` : 'Lining Colour');
        if (this.liningColourGrid) {
          if (this.renderedLiningColourKey !== (liningMaterial?.id || '')) {
            this.liningColourGrid.replaceChildren(...(liningMaterial ? liningColours.map((colour) => this.createLiningColourCard(liningMaterial, colour)) : []));
            this.renderedLiningColourKey = liningMaterial?.id || '';
          }
          setData(this.liningColourGrid, 'disabled', String(!liningMaterial));
        }
        if (this.liningColourHelper) {
          setText(this.liningColourHelper, liningMaterial
            ? `${liningColours.length} colours available for ${liningMaterial.label}.`
            : 'Colour choices are specific to each lining material.');
        }
      }
      const mainColour = material?.colourInventory.find((colour) => colour.id === this.rawState.materialColour) || null;
      if (this.keeperModeSelect) {
        this.keeperModeSelect.value = this.rawState.keeperColourMode || 'match-main';
        setFlag(this.keeperModeSelect, 'disabled', !mainColour);
      }
      const customKeepersActive = Boolean(mainColour && this.rawState.keeperColourMode === 'custom');
      if (this.keeperColours) setFlag(this.keeperColours, 'hidden', !customKeepersActive);
      if (this.keeperHelper) {
        setText(this.keeperHelper, !mainColour
          ? 'Choose the main leather and colour first.'
          : (customKeepersActive
            ? `Keeper colours are limited to ${material.label}. Match leather follows ${mainColour.label}.`
            : `Both keeper loops will follow ${material.label} in ${mainColour.label}.`));
      }
      const keeperGridKey = customKeepersActive ? `${material.id}:${mainColour.id}` : '';
      this.renderedKeeperGridKeys ||= {};
      Object.entries(this.keeperColourGrids).forEach(([field, grid]) => {
        if (!grid) return;
        if (this.renderedKeeperGridKeys[field] !== keeperGridKey) {
          const cards = customKeepersActive
            ? [this.createKeeperColourCard(material, mainColour, field, true), ...colours.map((colour) => this.createKeeperColourCard(material, colour, field, false))]
            : [];
          grid.replaceChildren(...cards);
          this.renderedKeeperGridKeys[field] = keeperGridKey;
        }
        setData(grid, 'disabled', String(!customKeepersActive));
        const current = this.root.querySelector(`[data-cs-cd-keeper-current="${field}"]`);
        const selected = this.rawState[field] === 'match-main'
          ? `Match leather (${mainColour?.label || ''})`
          : colours.find((colour) => colour.id === this.rawState[field])?.label || '';
        if (current) setText(current, selected ? ` — ${selected}` : '');
      });
    }

    setupTabNavigation() {
      if (!this.tabPanelsHost || !this.tabs.length) return;

      const stitchingRoot = this.root.querySelector('[data-cs-cd-panel-fields="stitching-edges"]');
      const edgeOptions = this.root.querySelector('[data-cs-cd-finishing-options="edgeFinishing"]');
      const nodes = (...items) => items.flat().filter(Boolean);
      const queryAll = (selector) => Array.from(this.root.querySelectorAll(selector));
      const contentByGroup = new Map([
        ['design', nodes(
          this.root.querySelector('.cs-cd__style-fieldset--strap'), this.styleZoom,
          this.root.querySelector('[data-cs-cd-outer-material]'),
          this.root.querySelector('[data-cs-cd-keepers]'),
          stitchingRoot?.querySelector(':scope > fieldset:first-of-type'), this.stitchingStyleZoom,
          this.root.querySelector('[data-cs-cd-thread-colour-fieldset]'), this.threadColourZoom,
          this.root.querySelector('[data-cs-cd-tip-shape-fieldset]'), this.tipShapeZoom,
          this.root.querySelector('[data-cs-cd-lining]')
        )],
        ['fit', nodes(
          this.root.querySelector('.cs-cd__style-fieldset--curved'),
          this.root.querySelector('.cs-cd__measurement-hooks'),
          this.root.querySelector('[data-cs-cd-tab-panel="dimensions"] > p:first-child'),
          this.root.querySelector('[data-cs-cd-panel-fields="dimensions"]'),
          this.root.querySelector('[data-cs-cd-buckle-group]'),
          this.root.querySelector('[data-cs-cd-spring-bars-fieldset]'),
          this.root.querySelector('[data-cs-cd-spring-bars-note]')
        )],
        ['construction', nodes(
          this.root.querySelector('.cs-cd__padding-fieldset'),
          queryAll('[data-cs-cd-thickness-fieldset]'),
          this.root.querySelector('[data-cs-cd-no-thickness-note]'),
          // Edge finishing is part of how the strap is built; it also keeps the Design tab from running much longer than the others.
          edgeOptions?.closest('fieldset'), this.root.querySelector('[data-cs-cd-edge-colour-fieldset]')
        )],
        ['finish', nodes(this.root.querySelector('[data-cs-cd-additional-customisation]'))]
      ]);

      GROUP_DEFINITIONS.forEach(({ id, label }) => {
        const content = contentByGroup.get(id) || [];
        if (!content.length) throw new Error(`Custom strap group "${id}" has no source content.`);
        const existingPanel = this.tabPanelsHost.querySelector(`[data-cs-cd-group-panel="${id}"]`);
        const panel = existingPanel || document.createElement('section');
        panel.id = `cs-cd-panel-${id}-${this.root.dataset.sectionId || 'section'}`;
        panel.className = 'cs-cd__tab-panel';
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `cs-cd-tab-${id}-${this.root.dataset.sectionId || 'section'}`);
        panel.setAttribute('aria-hidden', 'true');
        panel.tabIndex = -1;
        panel.dataset.csCdTabPanel = id;
        panel.dataset.csCdGroupPanel = id;
        panel.dataset.groupLabel = label;
        panel.hidden = true;
        // Liquid already placed the Design controls in this panel. Preserve
        // that DOM so hydration does not change its vertical geometry.
        if (!(id === 'design' && existingPanel)) content.forEach((node) => panel.append(node));
        if (!panel.isConnected) this.tabPanelsHost.append(panel);
        this.groupPanels.set(id, panel);
      });

      const serverSource = this.root.querySelector('[data-cs-cd-ssr-source]');
      const emptyPaddingGroup = serverSource?.querySelector('[data-cs-cd-validation-scope="padding-thickness"]');
      if (emptyPaddingGroup && !emptyPaddingGroup.children.length && !emptyPaddingGroup.textContent.trim()) {
        emptyPaddingGroup.remove();
      }
      serverSource?.querySelectorAll(':scope > .cs-cd__tab-panel').forEach((panel) => {
        if (!panel.children.length && !panel.textContent.trim()) {
          panel.remove();
          return;
        }
        panel.removeAttribute('id');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
        panel.removeAttribute('hidden');
      });
      this.root.querySelector('[data-cs-cd-legacy-source]')?.remove();
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.csCdTab));
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event));
      });
      // Returning with Back from /cart restores this page from the back/forward cache with the button still in
      // its "Added to cart" (disabled) state; reset it so the customer can add again.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || this.orderForm?.dataset.state !== 'success') return;
        this.setOrderState('idle', '');
        this.syncOrderAvailability();
      });
      this.previousButton?.addEventListener('click', () => this.moveTab(-1));
      this.nextButton?.addEventListener('click', () => {
        if (!this.validateTab(this.activeTabId, { focus: true })) return;
        this.confirmedGroups.add(this.activeTabId);
        this.renderTabStates();
        this.moveTab(1);
      });
    }

    activateTab(tabId, options) {
      const index = GROUP_DEFINITIONS.findIndex((group) => group.id === tabId);
      if (index < 0) return;
      this.activeTabId = tabId;
      this.tabs.forEach((tab) => {
        const active = tab.dataset.csCdTab === tabId;
        setAttr(tab, 'aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) {
          tab.parentElement?.scrollTo?.({
            left: Math.max(0, tab.offsetLeft - ((tab.parentElement.clientWidth - tab.offsetWidth) / 2)),
            behavior: options?.smooth ? 'smooth' : 'auto'
          });
          if (options?.focus !== false) tab.focus({ preventScroll: true });
        }
      });
      this.groupPanels.forEach((panel, id) => {
        const active = id === tabId;
        setFlag(panel, 'hidden', !active);
        setAttr(panel, 'aria-hidden', String(!active));
        panel.tabIndex = active ? 0 : -1;
      });
      if (this.previousButton) setFlag(this.previousButton, 'disabled', index === 0);
      if (this.nextButton) {
        const isLast = index === GROUP_DEFINITIONS.length - 1;
        setFlag(this.nextButton, 'hidden', isLast);
        setFlag(this.nextButton, 'disabled', isLast);
      }
      if (this.tabStatus) setText(this.tabStatus, `Step ${index + 1} of ${GROUP_DEFINITIONS.length}`);
      if (this.trackingArmed && index > 0) trackBuilderEvent(`step_${tabId}`);
    }

    moveTab(offset) {
      const index = GROUP_DEFINITIONS.findIndex((group) => group.id === this.activeTabId);
      const nextIndex = Math.max(0, Math.min(GROUP_DEFINITIONS.length - 1, index + offset));
      this.activateTab(GROUP_DEFINITIONS[nextIndex].id, { smooth: true });
      this.scrollTabsIntoView();
    }

    // After Previous/Next, bring the tab bar (and so the top of the new panel) just below the sticky UI,
    // on desktop and mobile. On mobile the target sits right under the compact preview bar, which is
    // exactly where that bar stays compact, so the expanded preview never covers the tabs.
    scrollTabsIntoView() {
      if (!this.tabsContainer) return;
      window.requestAnimationFrame(() => {
        // --sticky-area-height is a calc() expression, so resolve it through a hidden probe element.
        const probe = document.createElement('div');
        probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:var(--sticky-area-height, 0px)';
        this.root.appendChild(probe);
        const headerOffset = probe.offsetHeight;
        probe.remove();
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const preview = mobile ? this.root.querySelector('.cs-cd__panel-shell > [data-cs-cd-preview]') : null;
        let targetTop = headerOffset + 16;
        let visibleFrom = headerOffset;
        if (preview && !preview.hidden) {
          const previewTop = parseFloat(window.getComputedStyle(preview).top) || headerOffset;
          const compactHeight = parseFloat(window.getComputedStyle(this.root).getPropertyValue('--cs-cd-preview-compact-height')) || 72;
          targetTop = previewTop + compactHeight + 8;
          visibleFrom = preview.getBoundingClientRect().bottom;
        }
        const tabsTop = this.tabsContainer.getBoundingClientRect().top;
        // Already comfortably visible: don't move the page.
        if (tabsTop >= visibleFrom && tabsTop <= window.innerHeight * 0.45) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, window.scrollY + tabsTop - targetTop), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    }

    onTabKeydown(event) {
      const currentIndex = GROUP_DEFINITIONS.findIndex((group) => group.id === event.currentTarget.dataset.csCdTab);
      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % GROUP_DEFINITIONS.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + GROUP_DEFINITIONS.length) % GROUP_DEFINITIONS.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = GROUP_DEFINITIONS.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      this.activateTab(GROUP_DEFINITIONS[nextIndex].id, { smooth: true });
    }

    groupForField(field) {
      return GROUP_BY_FIELD.get(field) || null;
    }

    groupErrors(groupId) {
      const core = () => this.stateApi.validateCore(this.rawState, this.preconfigurationApi, this.styleDataApi);
      const finish = () => this.stateApi.validateFinishingHardware(this.rawState, this.finishingHardwareDataApi);
      const validators = {
        design: () => {
          const coreErrors = core();
          const finishingErrors = finish();
          return { style: coreErrors.style, ...this.stateApi.validateOuterMaterial(this.rawState, this.preconfigurationApi), ...this.stateApi.validateKeepers(this.rawState, this.preconfigurationApi), ...this.stateApi.validateStitching(this.rawState, this.stitchingDataApi), tipShape: finishingErrors.tipShape, ...this.stateApi.validateLining(this.rawState, this.liningDataApi) };
        },
        fit: () => ({ curvedEnd: core().curvedEnd, ...this.stateApi.validateDimensions(this.rawState, this.dimensionsDataApi), ...this.stateApi.validateBuckle(this.rawState, this.buckleDataApi), springBars: finish().springBars }),
        construction: () => {
          const finishingErrors = finish();
          return { ...this.stateApi.validatePaddingThickness(this.rawState, this.paddingThicknessDataApi), edgeFinishing: finishingErrors.edgeFinishing, edgeColour: finishingErrors.edgeColour };
        },
        finish: () => this.stateApi.validateAdditionalCustomisation(this.rawState, this.additionalCustomisationDataApi)
      };
      return validators[groupId]?.() || {};
    }

    groupHasErrors(groupId) {
      return this.hasValidationErrors(this.groupErrors(groupId));
    }

    validateTab(groupId, options) {
      this.attemptedGroups.add(groupId);
      const errors = this.groupErrors(groupId);
      if (this.hasValidationErrors(errors)) {
        if (['design', 'fit'].includes(groupId)) {
          this.coreValidationAttempted = true;
          this.renderCoreErrors(this.stateApi.validateCore(this.rawState, this.preconfigurationApi, this.styleDataApi));
        }
        if (groupId === 'design') { this.validateOuterMaterial(); this.validateKeepers(); this.validateStitching(); this.validateFinishingHardware(); this.validateLining(); }
        if (groupId === 'fit') { this.validateDimensions(); this.validateBuckle(); this.validateFinishingHardware(); }
        if (groupId === 'construction') { this.validatePaddingThickness(); this.validateFinishingHardware(); }
        if (groupId === 'finish') this.validateAdditionalCustomisation();
        const firstField = GROUP_DEFINITIONS.find((group) => group.id === groupId)?.fields.find((field) => errors[field]);
        if (firstField && options?.focus) this.focusField(firstField);
        this.renderTabStates();
        return false;
      }
      return true;
    }

    renderTabStates() {
      if (!this.tabs.length) return;
      this.groupPanels.forEach((_panel, id) => {
        const hasErrors = this.attemptedGroups.has(id) && this.groupHasErrors(id);
        const isComplete = this.confirmedGroups.has(id) && !hasErrors;
        const groupLabel = GROUP_DEFINITIONS.find((group) => group.id === id)?.label || id;
        const tab = this.root.querySelector(`[data-cs-cd-tab="${id}"]`);
        const status = this.root.querySelector(`[data-cs-cd-tab-state="${id}"]`);
        tab?.setAttribute('data-state', hasErrors ? 'error' : (isComplete ? 'complete' : 'pending'));
        tab?.setAttribute('aria-label', `${groupLabel}${hasErrors ? ', needs attention' : (isComplete ? ', completed' : '')}`);
        if (status) {
          setText(status, hasErrors ? '!' : (isComplete ? '✓' : ''));
          setAttr(status, 'aria-hidden', 'true');
          status.removeAttribute('aria-label');
        }
      });
    }
  }

  window.CernirCustomStrapController = CustomStrapCd;

  function init(container) {
    const stateApi = window.CernirCustomStrapState;
    const preconfigurationApi = window.CernirCustomStrapPreconfiguration;
    const styleDataApi = window.CernirCustomStrapStyleData;
    const liningDataApi = window.CernirCustomStrapLiningData;
    const dimensionsDataApi = window.CernirCustomStrapDimensionsData;
    const paddingThicknessDataApi = window.CernirCustomStrapPaddingThicknessData;
    const stitchingDataApi = window.CernirCustomStrapStitchingData;
    const finishingHardwareDataApi = window.CernirCustomStrapFinishingHardwareData;
    const buckleDataApi = window.CernirCustomStrapBuckleData;
    const additionalCustomisationDataApi = window.CernirCustomStrapAdditionalCustomisationData;
    const commerceApi = window.CernirCustomStrapCommerce;
    if (!stateApi) throw new Error('CERNIR custom strap state engine is unavailable.');
    if (!preconfigurationApi) throw new Error('CERNIR custom strap pre-configuration data is unavailable.');
    if (!styleDataApi) throw new Error('CERNIR custom strap style data is unavailable.');
    if (!liningDataApi) throw new Error('CERNIR custom strap lining data is unavailable.');
    if (!dimensionsDataApi) throw new Error('CERNIR custom strap dimensions data is unavailable.');
    if (!paddingThicknessDataApi) throw new Error('CERNIR custom strap padding and thickness data is unavailable.');
    if (!stitchingDataApi) throw new Error('CERNIR custom strap stitching data is unavailable.');
    if (!finishingHardwareDataApi) throw new Error('CERNIR custom strap finishing and hardware data is unavailable.');
    if (!buckleDataApi) throw new Error('CERNIR custom strap buckle data is unavailable.');
    if (!additionalCustomisationDataApi) throw new Error('CERNIR custom strap additional customisation data is unavailable.');
    if (!commerceApi) throw new Error('CERNIR custom strap commerce contract is unavailable.');

    (container || document).querySelectorAll('[data-cs-cd]').forEach((root) => {
      if (root.dataset.customStrapCdReady === 'true') return;
      try {
        new CustomStrapCd(root, stateApi, preconfigurationApi, styleDataApi, liningDataApi, dimensionsDataApi, paddingThicknessDataApi, stitchingDataApi, finishingHardwareDataApi, buckleDataApi, additionalCustomisationDataApi, commerceApi);
        root.dataset.customStrapCdReady = 'true';
      } catch (error) {
        root.dataset.customStrapCdError = error?.message || 'Unknown configurator error';
        console.error('CERNIR custom strap failed to initialize.', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
